import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { createAgent, getAgent, updateAgent, getAgentEditableFiles, updateAgentEditableFiles, getApiKeys, getProviderConnections, previewSchedulingAvailability, uploadAgentAvatar, deleteAgentAvatar, formatApiError, getOrganizationSchedulingSettings, getPromptImprovements, updatePromptImprovement } from '../lib/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { Slider } from '../components/ui/slider';
import { Badge } from '../components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { toast } from '../components/ui/sonner';
import {
  Bot, Headphones, Calendar, TrendingUp, GitBranch,
  ArrowLeft, Save, Loader2, Copy, Check, Plus, Trash2, Database,
  Settings2, Brain, MessageSquare, UserCheck, Puzzle, Code2, Zap,
  Clock, Shield, Globe, Hash, Mail, Phone, Building2, ExternalLink, FlaskConical, ImagePlus, X,
  AlertTriangle, ChevronDown, FileText
} from 'lucide-react';
import axios from 'axios';
import AgentAvatar from '../components/AgentAvatar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WEEKDAY_LABELS = [
  ['monday', 'Monday'],
  ['tuesday', 'Tuesday'],
  ['wednesday', 'Wednesday'],
  ['thursday', 'Thursday'],
  ['friday', 'Friday'],
  ['saturday', 'Saturday'],
  ['sunday', 'Sunday'],
];
const DEFAULT_WEEKLY_AVAILABILITY = {
  monday: { enabled: true, ranges: [{ start: '09:00', end: '17:00' }] },
  tuesday: { enabled: true, ranges: [{ start: '09:00', end: '17:00' }] },
  wednesday: { enabled: true, ranges: [{ start: '09:00', end: '17:00' }] },
  thursday: { enabled: true, ranges: [{ start: '09:00', end: '17:00' }] },
  friday: { enabled: true, ranges: [{ start: '09:00', end: '17:00' }] },
  saturday: { enabled: false, ranges: [{ start: '09:00', end: '17:00' }] },
  sunday: { enabled: false, ranges: [{ start: '09:00', end: '17:00' }] },
};

// ==================== SUB-COMPONENTS ====================

const SectionHeader = ({ icon: Icon, title, description }) => (
  <div className="flex items-start gap-3 mb-4">
    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
      <Icon className="w-4 h-4 text-primary" />
    </div>
    <div>
      <h3 className="font-semibold text-sm text-foreground">{title}</h3>
      {description && <p className="text-xs text-muted-foreground mt-0.5">{description}</p>}
    </div>
  </div>
);

const SliderField = ({ label, description, value, onChange, min, max, step, displayValue }) => (
  <div className="space-y-2">
    <div className="flex items-center justify-between">
      <div>
        <Label className="text-sm">{label}</Label>
        {description && <p className="text-xs text-muted-foreground">{description}</p>}
      </div>
      <span className="text-sm font-mono font-medium bg-secondary px-2 py-0.5 rounded text-foreground">{displayValue || value}</span>
    </div>
    <Slider
      value={[value]}
      onValueChange={(v) => onChange(v[0])}
      min={min}
      max={max}
      step={step}
      className="w-full"
    />
  </div>
);

const VariableTag = ({ name, onInsert }) => (
  <button
    type="button"
    onClick={() => onInsert(`{{${name}}}`)}
    className="inline-flex items-center gap-1 text-xs bg-primary/10 text-primary px-2 py-1 rounded-md hover:bg-primary/20 transition-colors font-mono"
    data-testid={`var-tag-${name}`}
  >
    <Hash className="w-3 h-3" />
    {name}
  </button>
);

const normalizeHexColor = (value, fallback) => {
  const raw = String(value || '').trim();
  if (/^#[0-9a-fA-F]{6}$/.test(raw)) return raw.toUpperCase();
  if (/^#[0-9a-fA-F]{3}$/.test(raw)) return `#${raw.slice(1).split('').map((c) => c + c).join('')}`.toUpperCase();
  return fallback.toUpperCase();
};

const hexToRgb = (hex) => {
  const safe = normalizeHexColor(hex, '#000000');
  return [1, 3, 5].map((idx) => parseInt(safe.slice(idx, idx + 2), 16));
};

const rgbToHex = (rgb) => `#${rgb.map((value) => Math.max(0, Math.min(255, Math.round(value))).toString(16).padStart(2, '0')).join('')}`.toUpperCase();

const mixHex = (a, b, amount) => {
  const clamped = Math.max(0, Math.min(1, Number(amount) || 0));
  const rgbA = hexToRgb(a);
  const rgbB = hexToRgb(b);
  return rgbToHex(rgbA.map((value, i) => value * (1 - clamped) + rgbB[i] * clamped));
};

const relativeLuminance = (hex) => {
  const [r, g, b] = hexToRgb(hex).map((value) => value / 255).map((value) => (value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

const contrastText = (hex, light = '#FFFFFF', dark = '#111827') => (relativeLuminance(hex) > 0.58 ? dark : light);

const parseListText = (text) => String(text || '').split(/\r?\n|,/).map(v => v.trim()).filter(Boolean);

const BuilderAdvancedSection = ({ title, summary, open, onOpenChange, children }) => (
  <Collapsible open={open} onOpenChange={onOpenChange}>
    <div className="rounded-xl border border-border bg-secondary/10 overflow-hidden">
      <CollapsibleTrigger asChild>
        <button type="button" className="w-full px-4 py-3 flex items-center justify-between gap-4 text-left hover:bg-secondary/20 transition-colors">
          <div>
            <p className="text-sm font-medium text-foreground">{title}</p>
            <p className="text-xs text-muted-foreground mt-1">{summary}</p>
          </div>
          <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="px-4 pb-4 pt-1 space-y-4 border-t border-border/60">{children}</div>
      </CollapsibleContent>
    </div>
  </Collapsible>
);

const deriveWidgetTheme = ({ primaryColor, themeMode, overrides = {} }) => {
  const primary = normalizeHexColor(primaryColor, '#9333EA');
  const mode = themeMode === 'dark' ? 'dark' : 'light';
  const defaults = mode === 'dark'
    ? {
        panel_background: '#111827',
        panel_border: '#374151',
        body_background: '#0F172A',
        body_text: '#F9FAFB',
        muted_text: '#9CA3AF',
        header_background: primary,
        header_text: contrastText(primary),
        assistant_bubble_background: '#1F2937',
        assistant_bubble_text: '#F9FAFB',
        user_bubble_background: primary,
        user_bubble_text: contrastText(primary),
        input_background: '#111827',
        input_text: '#F9FAFB',
        input_border: '#374151',
        send_button_background: primary,
        send_button_text: contrastText(primary),
        launcher_background: primary,
        launcher_icon: contrastText(primary),
        greeting_background: '#1F2937',
        greeting_text: '#F9FAFB',
        quick_select_background: mixHex(primary, '#111827', 0.78),
        quick_select_text: '#F9FAFB',
        quick_select_border: mixHex(primary, '#374151', 0.45),
      }
    : {
        panel_background: '#FFFFFF',
        panel_border: '#E5E7EB',
        body_background: '#FAFAFA',
        body_text: '#1F2937',
        muted_text: '#6B7280',
        header_background: primary,
        header_text: contrastText(primary),
        assistant_bubble_background: '#FFFFFF',
        assistant_bubble_text: '#1F2937',
        user_bubble_background: primary,
        user_bubble_text: contrastText(primary),
        input_background: '#FFFFFF',
        input_text: '#1F2937',
        input_border: '#E5E7EB',
        send_button_background: primary,
        send_button_text: contrastText(primary),
        launcher_background: primary,
        launcher_icon: contrastText(primary),
        greeting_background: '#FFFFFF',
        greeting_text: '#1F2937',
        quick_select_background: mixHex(primary, '#FFFFFF', 0.84),
        quick_select_text: '#111827',
        quick_select_border: mixHex(primary, '#FFFFFF', 0.72),
      };

  const theme = { ...defaults };
  Object.keys(defaults).forEach((key) => {
    theme[key] = normalizeHexColor(overrides[key], defaults[key]);
  });
  return { primary_color: primary, theme_mode: mode, ...theme };
};

// ==================== MAIN COMPONENT ====================

const AgentBuilderPage = () => {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const isEditing = !!agentId;
  const promptRef = useRef(null);
  const avatarInputRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [apiKeys, setApiKeys] = useState([]);
  const [copied, setCopied] = useState(false);
  const [knowledgeSources, setKnowledgeSources] = useState([]);
  const [knowledgeGroups, setKnowledgeGroups] = useState([]);
  const [linkedSources, setLinkedSources] = useState([]);
  const [kbMode, setKbMode] = useState('full');
  const [kbMaxResults, setKbMaxResults] = useState(5);
  const [kbNotes, setKbNotes] = useState('');
  const [sharedGroupKeys, setSharedGroupKeys] = useState([]);
  const [newKnowledgeGroupName, setNewKnowledgeGroupName] = useState('');
  const [creatingKnowledgeGroup, setCreatingKnowledgeGroup] = useState(false);
  const [activeTab, setActiveTab] = useState('identity');
  const [editableFiles, setEditableFiles] = useState({
    'SOUL.md': '',
    'IDENTITY.md': '',
    'HEARTBEAT.md': '',
    'TOOLS.md': '',
    'USER.md': '',
  });
  const [savingEditableFiles, setSavingEditableFiles] = useState(false);
  const [orgAgents, setOrgAgents] = useState([]); // All agents in the org for handoff dropdown
  const [providerConnections, setProviderConnections] = useState([]);
  const [organizationSchedulingSettings, setOrganizationSchedulingSettings] = useState(null);
  const [availabilityPreview, setAvailabilityPreview] = useState(null);
  const [previewingAvailability, setPreviewingAvailability] = useState(false);

  // ---- Identity & Personality ----
  const [name, setName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [handoffStyle, setHandoffStyle] = useState('announce_on_name_change');
  const [category, setCategory] = useState('customer_service');
  const [description, setDescription] = useState('');
  const [systemPrompt, setSystemPrompt] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [greetingMessage, setGreetingMessage] = useState('');
  const [tone, setTone] = useState('professional');
  const [responseLength, setResponseLength] = useState('balanced');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [primaryRole, setPrimaryRole] = useState('');
  const [targetAudience, setTargetAudience] = useState('');
  const [conversationPurpose, setConversationPurpose] = useState('');
  const [approvedTopics, setApprovedTopics] = useState([]);
  const [approvedTopicsText, setApprovedTopicsText] = useState('');
  const [outOfScopeTopics, setOutOfScopeTopics] = useState([]);
  const [outOfScopeTopicsText, setOutOfScopeTopicsText] = useState('');
  const [desiredPersonality, setDesiredPersonality] = useState('');
  const [directnessMode, setDirectnessMode] = useState('balanced');
  const [followUpFrequency, setFollowUpFrequency] = useState('only_when_necessary');
  const [maxFollowUpQuestions, setMaxFollowUpQuestions] = useState(1);
  const [formattingPreference, setFormattingPreference] = useState('plain_chat');
  const [expandOnlyIfAsked, setExpandOnlyIfAsked] = useState(true);
  const [unknownAnswerBehavior, setUnknownAnswerBehavior] = useState('admit_and_offer_help');
  const [knowledgeStrictness, setKnowledgeStrictness] = useState('business_with_light_general');
  const [brandVoiceRules, setBrandVoiceRules] = useState('');
  const [forbiddenPhrases, setForbiddenPhrases] = useState([]);
  const [forbiddenPhrasesText, setForbiddenPhrasesText] = useState('');
  const [exampleGoodResponses, setExampleGoodResponses] = useState([]);
  const [exampleBadResponses, setExampleBadResponses] = useState([]);

  // ---- AI Configuration ----
  const [llmProvider, setLlmProvider] = useState('openai');
  const [llmModel, setLlmModel] = useState('gpt-4.1-mini');
  const [usePlatformKey, setUsePlatformKey] = useState(true);
  const [customApiKeyId, setCustomApiKeyId] = useState('');
  const [modelAccessMode, setModelAccessMode] = useState('platform');
  const [modelProviderConnectionId, setModelProviderConnectionId] = useState('');
  const [fallbackModels, setFallbackModels] = useState([]);
  const [fallbackTriggers, setFallbackTriggers] = useState(['rate_limit', 'timeout', 'provider_error']);
  const [temperature, setTemperature] = useState(0.7);
  const [maxTokens, setMaxTokens] = useState(1024);
  const [topP, setTopP] = useState(1.0);

  // ---- Behavior ----
  const [maxConversationTurns, setMaxConversationTurns] = useState(50);
  const [conversationTimeoutMinutes, setConversationTimeoutMinutes] = useState(30);
  const [fallbackMessage, setFallbackMessage] = useState("I apologize, but I'm unable to help with that. Let me connect you with someone who can.");

  // ---- Contact Capture ----
  const [contactEnabled, setContactEnabled] = useState(true);
  const [contactFields, setContactFields] = useState(['name', 'email', 'phone']);
  const [contactRequired, setContactRequired] = useState(['name', 'email']);
  const [contactTiming, setContactTiming] = useState('start');
  const [contactWidgetMode, setContactWidgetMode] = useState('in_conversation');

  // ---- Dynamic Variables ----
  const [dynamicVariables, setDynamicVariables] = useState({});
  const [newVarKey, setNewVarKey] = useState('');
  const [newVarValue, setNewVarValue] = useState('');

  // ---- Category Settings ----
  // Customer Service
  const [escalationThreshold, setEscalationThreshold] = useState(5);
  const [businessHoursStart, setBusinessHoursStart] = useState('09:00');
  const [businessHoursEnd, setBusinessHoursEnd] = useState('17:00');
  const [businessTimezone, setBusinessTimezone] = useState('America/New_York');
  const [escalationMode, setEscalationMode] = useState('none');
  const [escalationEnabled, setEscalationEnabled] = useState(false);
  const [escalationEmails, setEscalationEmails] = useState('');
  const [escalationWebhookUrl, setEscalationWebhookUrl] = useState('');
  const [escalationWebhookSecret, setEscalationWebhookSecret] = useState('');
  const [escalationInternalNote, setEscalationInternalNote] = useState('');
  // Appointment Setting
  const [appointmentTimezone, setAppointmentTimezone] = useState('America/New_York');
  const [bookingUrl, setBookingUrl] = useState('');
  const [confirmationMessage, setConfirmationMessage] = useState('');
  const [useOrganizationSchedulingDefaults, setUseOrganizationSchedulingDefaults] = useState(true);
  const [schedulingProvider, setSchedulingProvider] = useState('booking_link');
  const [bookingMode, setBookingMode] = useState('link_only');
  const [providerConnectionId, setProviderConnectionId] = useState('');
  const [eventTypeId, setEventTypeId] = useState('');
  const [slotDurationMinutes, setSlotDurationMinutes] = useState(30);
  const [slotIncrementMinutes, setSlotIncrementMinutes] = useState(15);
  const [minimumNoticeMinutes, setMinimumNoticeMinutes] = useState(120);
  const [bufferBeforeMinutes, setBufferBeforeMinutes] = useState(15);
  const [bufferAfterMinutes, setBufferAfterMinutes] = useState(15);
  const [bookingWindowDays, setBookingWindowDays] = useState(30);
  const [businessHoursEnabled, setBusinessHoursEnabled] = useState(true);
  const [weeklyAvailability, setWeeklyAvailability] = useState(DEFAULT_WEEKLY_AVAILABILITY);
  const [dateOverrides, setDateOverrides] = useState([]);
  // Sales
  const [maxObjectionAttempts, setMaxObjectionAttempts] = useState(3);
  const [qualificationQuestions, setQualificationQuestions] = useState([]);
  const [newQualQuestion, setNewQualQuestion] = useState('');
  // Routing
  const [routingQuickSelectEnabled, setRoutingQuickSelectEnabled] = useState(false);
  const [routingQuickSelectOptions, setRoutingQuickSelectOptions] = useState([]);
  const [newQuickSelectLabel, setNewQuickSelectLabel] = useState('');
  const [newQuickSelectMessage, setNewQuickSelectMessage] = useState('');
  const [routingValidationError, setRoutingValidationError] = useState('');

  // ---- Widget Config ----
  const [bubbleAnimation, setBubbleAnimation] = useState('bounce');
  const [widgetPosition, setWidgetPosition] = useState('bottom-right');
  const [widgetColor, setWidgetColor] = useState('#9333EA');
  const [widgetThemeMode, setWidgetThemeMode] = useState('light');
  const [advancedStyling, setAdvancedStyling] = useState(false);
  const [typingAnimation, setTypingAnimation] = useState(true);
  const [typingPace, setTypingPace] = useState('normal');
  const [chunkResponses, setChunkResponses] = useState(true);
  const [widgetThemeOverrides, setWidgetThemeOverrides] = useState({});
  const [showAvatar, setShowAvatar] = useState(true);
  const [embedCode, setEmbedCode] = useState('');
  const [siteContextEnabled, setSiteContextEnabled] = useState(false);
  const [allowedContextKeys, setAllowedContextKeys] = useState('');
  const [webhookEnabled, setWebhookEnabled] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [webhookEvents, setWebhookEvents] = useState(['conversation.created', 'message.created', 'contact.updated']);

  // ---- Handoff Rules ----
  const [handoffRules, setHandoffRules] = useState([]);
  const [promptImprovements, setPromptImprovements] = useState([]);
  const [promptImprovementBusyId, setPromptImprovementBusyId] = useState(null);
  const [showAdvancedStyle, setShowAdvancedStyle] = useState(false);
  const [showAdvancedTruth, setShowAdvancedTruth] = useState(false);
  const [showBrandExamples, setShowBrandExamples] = useState(false);
  const [showDiagnosticsPreview, setShowDiagnosticsPreview] = useState(false);
  const [showPromptRefiner, setShowPromptRefiner] = useState(false);
  const [behaviorDefaultsTouched, setBehaviorDefaultsTouched] = useState(false);


  const categories = [
    { value: 'customer_service', label: 'General Business Assistant', icon: Headphones, color: 'hsl(var(--primary))', desc: 'Best default for one business agent that answers questions, handles support, and escalates when needed.' },
    { value: 'appointment_setting', label: 'Scheduling-Focused Assistant', icon: Calendar, color: '#22C55E', desc: 'Best when booking and availability are the main job, while still allowing normal conversation.' },
    { value: 'sales', label: 'Sales-Focused Assistant', icon: TrendingUp, color: '#F59E0B', desc: 'Best when qualification, objections, and conversion are the main goals.' },
    { value: 'routing', label: 'Advanced Routing Agent', icon: GitBranch, color: '#3B82F6', desc: 'Advanced setup for teams that truly need multi-agent routing and handoffs.' },
  ];

  const derivedWidgetTheme = useMemo(() => deriveWidgetTheme({
    primaryColor: widgetColor,
    themeMode: widgetThemeMode,
    overrides: advancedStyling ? widgetThemeOverrides : {},
  }), [widgetColor, widgetThemeMode, widgetThemeOverrides, advancedStyling]);

  const instructionConfig = useMemo(() => ({
    primary_role: primaryRole,
    target_audience: targetAudience,
    conversation_purpose: conversationPurpose,
    approved_topics: parseListText(approvedTopicsText),
    out_of_scope_topics: parseListText(outOfScopeTopicsText),
    desired_personality: desiredPersonality,
    directness_mode: directnessMode,
    follow_up_frequency: followUpFrequency,
    max_follow_up_questions: maxFollowUpQuestions,
    formatting_preference: formattingPreference,
    expand_only_if_asked: expandOnlyIfAsked,
    unknown_answer_behavior: unknownAnswerBehavior,
    knowledge_strictness: knowledgeStrictness,
    brand_voice_rules: brandVoiceRules,
    forbidden_phrases: parseListText(forbiddenPhrasesText),
    example_good_responses: exampleGoodResponses,
    example_bad_responses: exampleBadResponses,
  }), [approvedTopicsText, brandVoiceRules, conversationPurpose, desiredPersonality, directnessMode, exampleBadResponses, exampleGoodResponses, expandOnlyIfAsked, followUpFrequency, forbiddenPhrasesText, formattingPreference, knowledgeStrictness, maxFollowUpQuestions, outOfScopeTopicsText, primaryRole, targetAudience, unknownAnswerBehavior]);

  const styleSummary = useMemo(() => {
    return `${tone}, ${directnessMode.replace(/_/g, ' ')}, ${followUpFrequency.replace(/_/g, ' ')}, up to ${maxFollowUpQuestions} follow-up${maxFollowUpQuestions === 1 ? '' : 's'}`;
  }, [tone, directnessMode, followUpFrequency, maxFollowUpQuestions]);

  const truthSummary = useMemo(() => {
    return `${unknownAnswerBehavior.replace(/_/g, ' ')}, ${knowledgeStrictness.replace(/_/g, ' ')}`;
  }, [knowledgeStrictness, unknownAnswerBehavior]);

  const brandSummary = useMemo(() => {
    return `${forbiddenPhrases.length} forbidden phrase${forbiddenPhrases.length === 1 ? '' : 's'}, ${exampleGoodResponses.length} good example${exampleGoodResponses.length === 1 ? '' : 's'}, ${exampleBadResponses.length} bad example${exampleBadResponses.length === 1 ? '' : 's'}`;
  }, [exampleBadResponses.length, exampleGoodResponses.length, forbiddenPhrases.length]);

  const builderDiagnostics = useMemo(() => {
    const warnings = [];
    const conflicts = [];
    const roleLength = (primaryRole || '').trim().length;
    const purposeLength = (conversationPurpose || '').trim().length;
    const overlap = approvedTopics.filter((topic) => outOfScopeTopics.some((blocked) => blocked.toLowerCase() === topic.toLowerCase()));

    let scopeScore = 100;
    if (approvedTopics.length > 7) scopeScore -= 15;
    if (approvedTopics.length > 12) scopeScore -= 25;
    if (overlap.length > 0) scopeScore -= 20;
    if (outOfScopeTopics.length === 0) scopeScore -= 10;
    scopeScore = Math.max(0, scopeScore);

    let clarityScore = 100;
    if (roleLength < 12) clarityScore -= 20;
    if (purposeLength < 24) clarityScore -= 20;
    if (!targetAudience?.trim()) clarityScore -= 15;
    if (!brandVoiceRules?.trim()) clarityScore -= 10;
    if (!approvedTopics.length) clarityScore -= 15;
    if (!exampleGoodResponses.length) clarityScore -= 10;
    clarityScore = Math.max(0, clarityScore);

    if (approvedTopics.length > 7) warnings.push('This agent may be getting too broad. Consider narrowing the approved topics list.');
    if (approvedTopics.length > 12) warnings.push('Approved topics are well past the recommended range. This will be harder to control reliably.');
    if (!outOfScopeTopics.length) warnings.push('There are no out-of-scope topics yet, so the agent boundaries are still loose.');
    if (roleLength < 12) warnings.push('Primary role is still vague. Make it more specific so the agent has a tighter job definition.');
    if (purposeLength < 24) warnings.push('Conversation purpose is still vague. Spell out the actual job this agent should do.');
    if (!exampleGoodResponses.length) warnings.push('Add at least one example good response so the builder captures what “good” looks like.');
    if (overlap.length > 0) conflicts.push(`These topics are both approved and out of scope: ${overlap.join(', ')}`);
    if (followUpFrequency === 'rarely' && directnessMode === 'consultative') conflicts.push('Consultative mode conflicts with rarely asking follow-up questions.');
    if (responseLength === 'detailed' && expandOnlyIfAsked) conflicts.push('Detailed response preference conflicts with short-first / expand-only-if-asked.');
    if (knowledgeStrictness === 'strict_no_assumptions' && unknownAnswerBehavior === 'stay_narrow_and_redirect' && !fallbackMessage?.trim()) conflicts.push('Strict no-assumptions mode works better with a clear fallback message.');
    if (exampleGoodResponses.some((good) => exampleBadResponses.some((bad) => bad.trim().toLowerCase() === good.trim().toLowerCase()))) conflicts.push('One or more example responses appear in both good and bad lists.');

    return { scopeScore, clarityScore, warnings, conflicts };
  }, [approvedTopics, brandVoiceRules, conversationPurpose, directnessMode, exampleBadResponses, exampleGoodResponses, expandOnlyIfAsked, fallbackMessage, followUpFrequency, knowledgeStrictness, outOfScopeTopics, primaryRole, responseLength, targetAudience, unknownAnswerBehavior]);

  const generatedPromptPreview = useMemo(() => {
    const sections = [];
    const normalizedDescription = description?.trim();
    const normalizedPrompt = systemPrompt?.trim();
    const visibleName = displayName?.trim() || name?.trim() || 'This agent';

    sections.push(`You are ${visibleName}.`);

    if (normalizedDescription) {
      sections.push(`[CURRENT DESCRIPTION]\n${normalizedDescription}`);
    }

    sections.push(`[AGENT FOCUS]\nThis agent is configured as a ${category.replace(/_/g, ' ')} assistant.`);

    const identityLines = [];
    if (primaryRole?.trim()) identityLines.push(`Primary role: ${primaryRole.trim()}`);
    if (targetAudience?.trim()) identityLines.push(`Audience: ${targetAudience.trim()}`);
    if (conversationPurpose?.trim()) identityLines.push(`Conversation purpose: ${conversationPurpose.trim()}`);
    if (desiredPersonality?.trim()) identityLines.push(`Personality: ${desiredPersonality.trim()}`);
    if (identityLines.length > 0) {
      sections.push(`[BEHAVIOR CONFIG]\n- ${identityLines.join('\n- ')}`);
    }

    if ((approvedTopics || []).length > 0) {
      sections.push(`[APPROVED TOPICS]\n- ${(approvedTopics || []).join('\n- ')}`);
    }

    if ((outOfScopeTopics || []).length > 0) {
      sections.push(`[OUT OF SCOPE]\n- ${(outOfScopeTopics || []).join('\n- ')}`);
    }

    const styleLines = [];
    if (tone) styleLines.push(`Tone: ${tone}`);
    if (desiredPersonality?.trim()) styleLines.push(`Personality: ${desiredPersonality.trim()}`);
    if (responseLength) styleLines.push(`Response length: ${responseLength}`);
    if (directnessMode) styleLines.push(`Answer style: ${directnessMode.replace(/_/g, ' ')}`);
    if (followUpFrequency) styleLines.push(`Follow-up frequency: ${followUpFrequency.replace(/_/g, ' ')}`);
    styleLines.push(`Max follow-up questions at once: ${maxFollowUpQuestions}`);
    if (formattingPreference) styleLines.push(`Formatting preference: ${formattingPreference.replace(/_/g, ' ')}`);
    styleLines.push(`Expand only if asked: ${expandOnlyIfAsked ? 'yes' : 'no'}`);
    if (styleLines.length > 0) {
      sections.push(`[CONVERSATION STYLE]\n- ${styleLines.join('\n- ')}`);
    }

    const truthLines = [];
    if (unknownAnswerBehavior) truthLines.push(`Unknown-answer behavior: ${unknownAnswerBehavior.replace(/_/g, ' ')}`);
    if (knowledgeStrictness) truthLines.push(`Knowledge strictness: ${knowledgeStrictness.replace(/_/g, ' ')}`);
    if (truthLines.length > 0) {
      sections.push(`[TRUTH & KNOWLEDGE POLICY]\n- ${truthLines.join('\n- ')}`);
    }

    if (brandVoiceRules?.trim()) {
      sections.push(`[BRAND VOICE RULES]\n${brandVoiceRules.trim()}`);
    }

    if ((forbiddenPhrases || []).length > 0) {
      sections.push(`[FORBIDDEN PHRASES]\n- ${(forbiddenPhrases || []).join('\n- ')}`);
    }

    if ((exampleGoodResponses || []).length > 0) {
      sections.push(`[EXAMPLE GOOD RESPONSES]\n- ${(exampleGoodResponses || []).join('\n- ')}`);
    }

    if ((exampleBadResponses || []).length > 0) {
      sections.push(`[EXAMPLE BAD RESPONSES]\n- ${(exampleBadResponses || []).join('\n- ')}`);
    }

    if (normalizedPrompt) {
      sections.push(`[CUSTOM INSTRUCTIONS]\n${normalizedPrompt}`);
    }

    if (Object.keys(dynamicVariables || {}).length > 0) {
      const dynVarLines = Object.entries(dynamicVariables)
        .map(([key, value]) => `- ${key}: ${value}`)
        .join('\n');
      sections.push(`[BUSINESS VARIABLES]\n${dynVarLines}`);
    }

    if (contactEnabled) {
      const contactLines = [
        `Fields allowed: ${(contactFields || []).join(', ') || 'none'}`,
        `Required fields: ${(contactRequired || []).join(', ') || 'none'}`,
        `Timing: ${contactTiming}`,
      ];
      sections.push(`[CONTACT CAPTURE]\n- ${contactLines.join('\n- ')}`);
    }

    const runtimeLines = [
      `[TONE] and [RESPONSE LENGTH] instructions are injected from the builder settings above.`,
      `[CONVERSATION STYLE RULES] are still added by Apollo runtime for brevity, anti-repetition, and live-chat behavior.`,
      `[KB STRATEGY] is injected from the linked knowledge configuration and current KB mode.`,
      `[CATEGORY RULES] such as sales objection handling, customer-service escalation, and scheduling workflow are injected from category settings.`,
      `[HANDOFF DETECTION] and deployment context are added at runtime when relevant.`,
    ];
    sections.push(`[RUNTIME NOTE]\n- ${runtimeLines.join('\n- ')}`);

    return sections.filter(Boolean).join('\n\n');
  }, [approvedTopics, brandVoiceRules, category, contactEnabled, contactFields, contactRequired, contactTiming, conversationPurpose, desiredPersonality, description, directnessMode, displayName, dynamicVariables, exampleBadResponses, exampleGoodResponses, expandOnlyIfAsked, followUpFrequency, forbiddenPhrases, formattingPreference, knowledgeStrictness, maxFollowUpQuestions, name, outOfScopeTopics, primaryRole, responseLength, systemPrompt, targetAudience, tone, unknownAnswerBehavior]);

  const updateWidgetThemeOverride = (key, value) => {
    setWidgetThemeOverrides((current) => ({ ...current, [key]: value }));
  };

  const resetWidgetThemeOverrides = () => {
    setWidgetThemeOverrides({});
  };

  const applyCategoryDefaults = useCallback((nextCategory) => {
    const defaultsByCategory = {
      customer_service: {
        directnessMode: 'balanced',
        followUpFrequency: 'only_when_necessary',
        maxFollowUpQuestions: 1,
        formattingPreference: 'plain_chat',
        expandOnlyIfAsked: true,
        unknownAnswerBehavior: 'admit_and_offer_help',
        knowledgeStrictness: 'business_with_light_general',
      },
      sales: {
        directnessMode: 'consultative',
        followUpFrequency: 'often',
        maxFollowUpQuestions: 2,
        formattingPreference: 'short_paragraphs',
        expandOnlyIfAsked: true,
        unknownAnswerBehavior: 'admit_and_offer_help',
        knowledgeStrictness: 'business_with_light_general',
      },
      appointment_setting: {
        directnessMode: 'direct',
        followUpFrequency: 'only_when_necessary',
        maxFollowUpQuestions: 1,
        formattingPreference: 'plain_chat',
        expandOnlyIfAsked: true,
        unknownAnswerBehavior: 'stay_narrow_and_redirect',
        knowledgeStrictness: 'business_only',
      },
      routing: {
        directnessMode: 'direct',
        followUpFrequency: 'only_when_necessary',
        maxFollowUpQuestions: 1,
        formattingPreference: 'plain_chat',
        expandOnlyIfAsked: true,
        unknownAnswerBehavior: 'stay_narrow_and_redirect',
        knowledgeStrictness: 'strict_no_assumptions',
      },
    };
    const defaults = defaultsByCategory[nextCategory];
    if (!defaults || behaviorDefaultsTouched) return;
    setDirectnessMode(defaults.directnessMode);
    setFollowUpFrequency(defaults.followUpFrequency);
    setMaxFollowUpQuestions(defaults.maxFollowUpQuestions);
    setFormattingPreference(defaults.formattingPreference);
    setExpandOnlyIfAsked(defaults.expandOnlyIfAsked);
    setUnknownAnswerBehavior(defaults.unknownAnswerBehavior);
    setKnowledgeStrictness(defaults.knowledgeStrictness);
  }, [behaviorDefaultsTouched]);

  const loadPromptImprovements = async (currentAgentId) => {
    if (!currentAgentId) {
      setPromptImprovements([]);
      return;
    }
    try {
      const res = await getPromptImprovements();
      setPromptImprovements((res.data || []).filter((item) => item.agent_id === currentAgentId));
    } catch (error) {
      console.error('Failed to load prompt improvements:', error);
    }
  };

  const applyPromptImprovement = async (item) => {
    setPromptImprovementBusyId(item.id);
    try {
      const patch = (item.suggested_prompt_patch || '').trim();
      if (patch) {
        setSystemPrompt((current) => {
          const base = (current || '').trim();
          return base ? `${base}\n\n[Applied refinement]\n${patch}` : `[Applied refinement]\n${patch}`;
        });
      }
      await updatePromptImprovement(item.id, { applied: true });
      await loadPromptImprovements(agentId);
      toast.success('Prompt improvement applied to custom instructions draft. Save the agent to persist it.');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setPromptImprovementBusyId(null);
    }
  };

  const dismissPromptImprovement = async (item) => {
    setPromptImprovementBusyId(item.id);
    try {
      await updatePromptImprovement(item.id, { status: 'dismissed' });
      await loadPromptImprovements(agentId);
      toast.success('Prompt improvement dismissed.');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setPromptImprovementBusyId(null);
    }
  };

  const createKnowledgeGroupInline = async () => {
    const name = newKnowledgeGroupName.trim();
    if (!name) return;
    setCreatingKnowledgeGroup(true);
    try {
      const res = await axios.post(`${API}/knowledge-groups`, { name }, { withCredentials: true });
      const created = res.data;
      setKnowledgeGroups((current) => [...current, created].sort((a, b) => a.name.localeCompare(b.name)));
      setSharedGroupKeys((current) => current.includes(created.group_key) ? current : [...current, created.group_key]);
      setNewKnowledgeGroupName('');
      toast.success(`Created group ${created.name}`);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setCreatingKnowledgeGroup(false);
    }
  };

  const providers = [
    { value: 'anthropic', label: 'Anthropic' },
    { value: 'openai', label: 'OpenAI' },
    { value: 'gemini', label: 'Google Gemini' },
  ];

  const modelsByProvider = useMemo(() => ({
    anthropic: [
      { value: 'claude-sonnet-4-5-20250929', label: 'Claude Sonnet 4.5' },
      { value: 'claude-4-sonnet-20250514', label: 'Claude 4 Sonnet' },
      { value: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5' },
    ],
    openai: [
      { value: 'gpt-4.1-mini', label: 'GPT-4.1 Mini' },
      { value: 'gpt-5.2', label: 'GPT-5.2' },
      { value: 'gpt-5.1', label: 'GPT-5.1' },
      { value: 'gpt-4o', label: 'GPT-4o' },
    ],
    gemini: [
      { value: 'gemini-3-flash-preview', label: 'Gemini 3 Flash' },
      { value: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro' },
    ],
  }), []);

  const tones = [
    { value: 'professional', label: 'Professional', desc: 'Courteous and business-appropriate' },
    { value: 'friendly', label: 'Friendly', desc: 'Warm and approachable' },
    { value: 'casual', label: 'Casual', desc: 'Relaxed and conversational' },
    { value: 'formal', label: 'Formal', desc: 'Highly professional demeanor' },
  ];

  const defaultPrompts = useMemo(() => ({
    customer_service: `You are a helpful customer service agent for {{business_name}}.

Your role:
- Greet customers warmly and identify their issue
- Search the knowledge base for relevant answers
- Provide clear, accurate solutions
- If you cannot resolve within the escalation limit, offer to connect them with a human agent

Always be patient, empathetic, and solution-oriented.`,
    appointment_setting: `You are an appointment scheduling assistant for {{business_name}}.

Your role:
- Help users find available time slots
- Collect necessary booking information
- Confirm appointments with all relevant details
- Handle rescheduling and cancellation requests

Be efficient, organized, and confirm all details before finalizing.`,
    sales: `You are a sales assistant for {{business_name}}.

Your role:
- Understand the prospect's needs through qualifying questions
- Present relevant products/services that match their needs
- Address objections thoughtfully (respect the objection attempt limit)
- Guide prospects toward a buying decision without being pushy

Be consultative, knowledgeable, and customer-focused.`,
    routing: `You are the front-door assistant for {{business_name}}.

Your role:
- Greet the visitor warmly and briefly
- Identify what they need in one or two questions at most
- Route them to the correct specialist agent based on their intent
- Do NOT attempt to resolve issues yourself — your job is accurate routing

Available routes and when to use them are defined in your Handoff Rules. Always route; never try to handle the conversation end-to-end.`
  }), []);

  const allContactFields = [
    { key: 'name', label: 'Full Name', icon: UserCheck },
    { key: 'email', label: 'Email', icon: Mail },
    { key: 'phone', label: 'Phone', icon: Phone },
    { key: 'company', label: 'Company', icon: Building2 },
  ];

  const timezones = [
    'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
    'America/Phoenix', 'America/Anchorage', 'Pacific/Honolulu',
    'Europe/London', 'Europe/Paris', 'Europe/Berlin',
    'Asia/Tokyo', 'Asia/Shanghai', 'Asia/Kolkata',
    'Australia/Sydney', 'UTC'
  ];

  const providerApiKeys = useMemo(
    () => apiKeys.filter(k => k.provider === llmProvider),
    [apiKeys, llmProvider]
  );

  const openAIConnectedAccountConnections = useMemo(
    () => providerConnections.filter((connection) => connection.provider === 'openai_connected_account' && connection.status === 'active'),
    [providerConnections]
  );

  const appointmentCategorySettings = category === 'appointment_setting' ? {
    use_organization_defaults: useOrganizationSchedulingDefaults,
    timezone: appointmentTimezone,
    booking_url: bookingUrl,
    confirmation_message: confirmationMessage,
    scheduling_provider: schedulingProvider,
    booking_mode: bookingMode,
    provider_connection_id: providerConnectionId,
    event_type_id: eventTypeId,
    slot_duration_minutes: slotDurationMinutes,
    slot_increment_minutes: slotIncrementMinutes,
    minimum_notice_minutes: minimumNoticeMinutes,
    buffer_before_minutes: bufferBeforeMinutes,
    buffer_after_minutes: bufferAfterMinutes,
    booking_window_days: bookingWindowDays,
    business_hours_enabled: businessHoursEnabled,
    business_hours: {
      start: businessHoursStart,
      end: businessHoursEnd,
      timezone: appointmentTimezone,
    },
    weekly_availability: weeklyAvailability,
    date_overrides: dateOverrides,
  } : null;

  // ==================== EFFECTS ====================

  const handlePreviewAvailability = async () => {
    if (category !== 'appointment_setting') return;
    setPreviewingAvailability(true);
    try {
      const res = await previewSchedulingAvailability({
        provider_connection_id: providerConnectionId || null,
        category_settings: appointmentCategorySettings || {}
      });
      setAvailabilityPreview(res.data);
      toast.success(`Found ${res.data.slot_count || 0} preview slots`);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setPreviewingAvailability(false);
    }
  };


  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [keysRes, sourcesRes, agentsRes, providerConnectionsRes, organizationSchedulingRes, groupsRes] = await Promise.allSettled([
          getApiKeys(),
          axios.get(`${API}/knowledge-sources`, { withCredentials: true }),
          axios.get(`${API}/agents`, { withCredentials: true }),
          getProviderConnections(),
          getOrganizationSchedulingSettings(),
          axios.get(`${API}/knowledge-groups`, { withCredentials: true })
        ]);
        setApiKeys(keysRes.status === 'fulfilled' ? keysRes.value.data : []);
        setKnowledgeSources(sourcesRes.status === 'fulfilled' ? (sourcesRes.value.data || []) : []);
        setKnowledgeGroups(groupsRes.status === 'fulfilled' ? (groupsRes.value.data || []) : []);
        setOrgAgents(agentsRes.status === 'fulfilled' ? (agentsRes.value.data || []) : []);
        setProviderConnections(providerConnectionsRes.status === 'fulfilled' ? (providerConnectionsRes.value.data || []) : []);
        const orgSchedulingData = organizationSchedulingRes.status === 'fulfilled' ? (organizationSchedulingRes.value.data?.settings || null) : null;
        setOrganizationSchedulingSettings(orgSchedulingData);

        if (isEditing) {
          const [agentRes, linkedRes, editableFilesRes] = await Promise.allSettled([
            getAgent(agentId),
            axios.get(`${API}/agents/${agentId}/knowledge-sources`, { withCredentials: true }),
            getAgentEditableFiles(agentId),
          ]);
          if (agentRes.status !== 'fulfilled') {
            throw agentRes.reason;
          }
          const d = agentRes.value.data;
          setName(d.name);
          setDisplayName(d.display_name || d.display_name_effective || d.name || '');
          setHandoffStyle(d.handoff_style || 'announce_on_name_change');
          setCategory(d.category);
          setDescription(d.description || '');
          setSystemPrompt(d.system_prompt || '');
          setLlmProvider(d.llm_provider);
          setLlmModel(d.llm_model);
          setUsePlatformKey(d.use_platform_key);
          setCustomApiKeyId(d.custom_api_key_id || '');
          setModelAccessMode(d.model_access_mode || (d.use_platform_key ? 'platform' : 'api_key'));
          setModelProviderConnectionId(d.model_provider_connection_id || '');
          setFallbackModels(d.fallback_models || []);
          setFallbackTriggers(d.fallback_triggers || ['rate_limit', 'timeout', 'provider_error']);
          setIsActive(d.is_active);
          setEmbedCode(d.embed_code || '');
          setHandoffRules(d.handoff_rules || []);
          setLinkedSources(linkedRes.status === 'fulfilled' ? linkedRes.value.data.map(s => s.id) : []);
          const kb = d.knowledge_base_config || {};
          setKbMode(kb.mode || (d.category === 'routing' ? 'minimal' : d.category === 'appointment_setting' ? 'scheduling_focused' : 'full'));
          setKbMaxResults(kb.max_results ?? (d.category === 'routing' ? 2 : d.category === 'appointment_setting' ? 3 : 5));
          setKbNotes(kb.notes || '');
          setSharedGroupKeys(kb.shared_group_keys || []);
          // V2 fields
          setTemperature(d.temperature ?? 0.7);
          setMaxTokens(d.max_tokens ?? 1024);
          setTopP(d.top_p ?? 1.0);
          setGreetingMessage(d.greeting_message || '');
          setTone(d.tone || 'professional');
          setResponseLength(d.response_length || 'balanced');
          setAvatarUrl(d.avatar_url || '');
          setMaxConversationTurns(d.max_conversation_turns ?? 50);
          setConversationTimeoutMinutes(d.conversation_timeout_minutes ?? 30);
          setFallbackMessage(d.fallback_message || "I apologize, but I'm unable to help with that. Let me connect you with someone who can.");
          // Contact config
          const cc = d.contact_config || {};
          setContactEnabled(cc.enabled ?? true);
          setContactFields(cc.fields || ['name', 'email', 'phone']);
          setContactRequired(cc.required_fields || ['name', 'email']);
          setContactTiming(cc.timing || 'start');
          setContactWidgetMode(cc.widget_mode || 'in_conversation');
          // Widget config
          const wc = d.widget_config || {};
          setBubbleAnimation(wc.bubble_animation || 'bounce');
          setWidgetPosition(wc.position || 'bottom-right');
          setWidgetColor(wc.primary_color || '#9333EA');
          setWidgetThemeMode(wc.theme_mode || 'light');
          setAdvancedStyling(!!wc.advanced_styling);
          setTypingAnimation(wc.typing_animation ?? true);
          setTypingPace(wc.typing_pace || 'normal');
          setChunkResponses(wc.chunk_responses ?? true);
          setWidgetThemeOverrides({
            panel_background: wc.panel_background || '',
            panel_border: wc.panel_border || '',
            body_background: wc.body_background || '',
            body_text: wc.body_text || '',
            muted_text: wc.muted_text || '',
            header_background: wc.header_background || '',
            header_text: wc.header_text || '',
            assistant_bubble_background: wc.assistant_bubble_background || '',
            assistant_bubble_text: wc.assistant_bubble_text || '',
            user_bubble_background: wc.user_bubble_background || '',
            user_bubble_text: wc.user_bubble_text || '',
            input_background: wc.input_background || '',
            input_text: wc.input_text || '',
            input_border: wc.input_border || '',
            send_button_background: wc.send_button_background || '',
            send_button_text: wc.send_button_text || '',
            launcher_background: wc.launcher_background || '',
            launcher_icon: wc.launcher_icon || '',
            greeting_background: wc.greeting_background || '',
            greeting_text: wc.greeting_text || '',
            quick_select_background: wc.quick_select_background || '',
            quick_select_text: wc.quick_select_text || '',
            quick_select_border: wc.quick_select_border || '',
          });
          setShowAvatar(wc.show_avatar ?? true);
          // Dynamic variables
          setDynamicVariables(d.dynamic_variables || {});
          setEditableFiles(editableFilesRes.status === 'fulfilled'
            ? (editableFilesRes.value.data?.files || d.editable_files || {
                'SOUL.md': '',
                'IDENTITY.md': '',
                'HEARTBEAT.md': '',
                'TOOLS.md': '',
                'USER.md': '',
              })
            : (d.editable_files || {
                'SOUL.md': '',
                'IDENTITY.md': '',
                'HEARTBEAT.md': '',
                'TOOLS.md': '',
                'USER.md': '',
              }));
          const ic = d.instruction_config || {};
          setPrimaryRole(ic.primary_role || '');
          setTargetAudience(ic.target_audience || '');
          setConversationPurpose(ic.conversation_purpose || '');
          setApprovedTopics(ic.approved_topics || []);
          setApprovedTopicsText((ic.approved_topics || []).join('\n'));
          setOutOfScopeTopics(ic.out_of_scope_topics || []);
          setOutOfScopeTopicsText((ic.out_of_scope_topics || []).join('\n'));
          setDesiredPersonality(ic.desired_personality || '');
          setDirectnessMode(ic.directness_mode || 'balanced');
          setFollowUpFrequency(ic.follow_up_frequency || 'only_when_necessary');
          setMaxFollowUpQuestions(ic.max_follow_up_questions || 1);
          setFormattingPreference(ic.formatting_preference || 'plain_chat');
          setExpandOnlyIfAsked(ic.expand_only_if_asked ?? true);
          setUnknownAnswerBehavior(ic.unknown_answer_behavior || 'admit_and_offer_help');
          setKnowledgeStrictness(ic.knowledge_strictness || 'business_with_light_general');
          setBrandVoiceRules(ic.brand_voice_rules || '');
          setForbiddenPhrases(ic.forbidden_phrases || []);
          setForbiddenPhrasesText((ic.forbidden_phrases || []).join('\n'));
          setExampleGoodResponses(ic.example_good_responses || []);
          setExampleBadResponses(ic.example_bad_responses || []);
          // Category settings
          const cs = d.category_settings || {};
          await loadPromptImprovements(agentId);
          if (d.category === 'customer_service') {
            setEscalationThreshold(cs.escalation_threshold ?? 5);
            setBusinessHoursStart(cs.business_hours?.start || '09:00');
            setBusinessHoursEnd(cs.business_hours?.end || '17:00');
            setBusinessTimezone(cs.business_hours?.timezone || 'America/New_York');
          } else if (d.category === 'appointment_setting') {
            const orgScheduling = orgSchedulingData || {};
            const useOrgDefaults = cs.use_organization_defaults ?? true;
            const effectiveScheduling = useOrgDefaults ? {
              ...orgScheduling,
              confirmation_message: cs.confirmation_message || ''
            } : cs;
            setUseOrganizationSchedulingDefaults(useOrgDefaults);
            setAppointmentTimezone(effectiveScheduling.timezone || 'America/New_York');
            setBookingUrl(effectiveScheduling.booking_url || '');
            setConfirmationMessage(cs.confirmation_message || '');
            setSchedulingProvider(effectiveScheduling.scheduling_provider || 'booking_link');
            setBookingMode(effectiveScheduling.booking_mode || 'link_only');
            setProviderConnectionId(effectiveScheduling.provider_connection_id || '');
            setEventTypeId(effectiveScheduling.event_type_id || '');
            setSlotDurationMinutes(effectiveScheduling.slot_duration_minutes ?? 30);
            setSlotIncrementMinutes(effectiveScheduling.slot_increment_minutes ?? 15);
            setMinimumNoticeMinutes(effectiveScheduling.minimum_notice_minutes ?? 120);
            setBufferBeforeMinutes(effectiveScheduling.buffer_before_minutes ?? 15);
            setBufferAfterMinutes(effectiveScheduling.buffer_after_minutes ?? 15);
            setBookingWindowDays(effectiveScheduling.booking_window_days ?? 30);
            setBusinessHoursEnabled(effectiveScheduling.business_hours_enabled ?? true);
            setBusinessHoursStart(effectiveScheduling.business_hours?.start || '09:00');
            setBusinessHoursEnd(effectiveScheduling.business_hours?.end || '17:00');
            setWeeklyAvailability(effectiveScheduling.weekly_availability || orgScheduling.weekly_availability || DEFAULT_WEEKLY_AVAILABILITY);
            setDateOverrides(effectiveScheduling.date_overrides || []);
          } else if (d.category === 'sales') {
            setMaxObjectionAttempts(cs.max_objection_attempts ?? 3);
            setQualificationQuestions(cs.qualification_questions || []);
          } else if (d.category === 'routing') {
            setRoutingQuickSelectEnabled(cs.quick_selects_enabled ?? cs.quick_select_enabled ?? false);
            setRoutingQuickSelectOptions(cs.quick_selects || cs.quick_select_options || []);
          }
          const ec = d.escalation_config || {};
          setEscalationEnabled(!!ec.enabled);
          setEscalationMode(ec.mode || 'none');
          setEscalationEmails((ec.email_recipients || []).join(', '));
          setEscalationWebhookUrl(ec.webhook_url || '');
          setEscalationWebhookSecret(ec.webhook_secret || '');
          setEscalationInternalNote(ec.internal_note || '');
          setSiteContextEnabled(!!wc.site_context_enabled);
          setAllowedContextKeys((wc.allowed_context_keys || []).join(', '));
          setWebhookEnabled(!!wc.webhook_enabled);
          setWebhookUrl(wc.webhook_url || '');
          setWebhookSecret(wc.webhook_secret || '');
          setWebhookEvents(wc.webhook_events || ['conversation.created', 'message.created', 'contact.updated']);
        }
      } catch (e) {
        console.error('Failed to fetch data:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [agentId, isEditing]);

  useEffect(() => {
    if (!isEditing && !systemPrompt) {
      setSystemPrompt(defaultPrompts[category] || '');
    }
  }, [category, defaultPrompts, isEditing, systemPrompt]);

  useEffect(() => {
    const models = modelsByProvider[llmProvider] || [];
    if (models.length > 0 && !models.find(m => m.value === llmModel)) {
      setLlmModel(models[0].value);
    }
  }, [llmProvider, llmModel, modelsByProvider]);

  useEffect(() => {
    if (usePlatformKey) {
      if (customApiKeyId) setCustomApiKeyId('');
      return;
    }

    if (customApiKeyId && !providerApiKeys.some(k => k.id === customApiKeyId)) {
      setCustomApiKeyId('');
    }
  }, [usePlatformKey, customApiKeyId, providerApiKeys]);

  useEffect(() => {
    if (!isEditing) {
      applyCategoryDefaults(category);
    }
  }, [applyCategoryDefaults, category, isEditing]);

  useEffect(() => {
    if (category !== 'appointment_setting' || !useOrganizationSchedulingDefaults || !organizationSchedulingSettings) return;
    setAppointmentTimezone(organizationSchedulingSettings.timezone || 'America/New_York');
    setBookingUrl(organizationSchedulingSettings.booking_url || '');
    setSchedulingProvider(organizationSchedulingSettings.scheduling_provider || 'booking_link');
    setBookingMode(organizationSchedulingSettings.booking_mode || 'link_only');
    setProviderConnectionId(organizationSchedulingSettings.provider_connection_id || '');
    setEventTypeId(organizationSchedulingSettings.event_type_id || '');
    setSlotDurationMinutes(organizationSchedulingSettings.slot_duration_minutes ?? 30);
    setSlotIncrementMinutes(organizationSchedulingSettings.slot_increment_minutes ?? 15);
    setMinimumNoticeMinutes(organizationSchedulingSettings.minimum_notice_minutes ?? 120);
    setBufferBeforeMinutes(organizationSchedulingSettings.buffer_before_minutes ?? 15);
    setBufferAfterMinutes(organizationSchedulingSettings.buffer_after_minutes ?? 15);
    setBookingWindowDays(organizationSchedulingSettings.booking_window_days ?? 30);
    setBusinessHoursEnabled(organizationSchedulingSettings.business_hours_enabled ?? true);
    setBusinessHoursStart(organizationSchedulingSettings.business_hours?.start || '09:00');
    setBusinessHoursEnd(organizationSchedulingSettings.business_hours?.end || '17:00');
    setWeeklyAvailability(organizationSchedulingSettings.weekly_availability || DEFAULT_WEEKLY_AVAILABILITY);
    setDateOverrides(organizationSchedulingSettings.date_overrides || []);
  }, [category, useOrganizationSchedulingDefaults, organizationSchedulingSettings]);


  // ==================== HANDLERS ====================

  const buildCategorySettings = () => {
    if (category === 'customer_service') {
      return {
        escalation_threshold: escalationThreshold,
        business_hours: { start: businessHoursStart, end: businessHoursEnd, timezone: businessTimezone }
      };
    } else if (category === 'appointment_setting') {
      if (useOrganizationSchedulingDefaults) {
        return {
          use_organization_defaults: true,
          confirmation_message: confirmationMessage,
        };
      }
      return {
        use_organization_defaults: false,
        timezone: appointmentTimezone,
        booking_url: bookingUrl,
        confirmation_message: confirmationMessage,
        scheduling_provider: schedulingProvider,
        booking_mode: bookingMode,
        provider_connection_id: providerConnectionId,
        event_type_id: eventTypeId,
        slot_duration_minutes: slotDurationMinutes,
        slot_increment_minutes: slotIncrementMinutes,
        minimum_notice_minutes: minimumNoticeMinutes,
        buffer_before_minutes: bufferBeforeMinutes,
        buffer_after_minutes: bufferAfterMinutes,
        booking_window_days: bookingWindowDays,
        business_hours_enabled: businessHoursEnabled,
        business_hours: {
          start: businessHoursStart,
          end: businessHoursEnd,
          timezone: appointmentTimezone,
        },
        weekly_availability: weeklyAvailability,
        date_overrides: dateOverrides,
      };
    } else if (category === 'sales') {
      return {
        max_objection_attempts: maxObjectionAttempts,
        qualification_questions: qualificationQuestions
      };
    } else if (category === 'routing') {
      return {
        quick_selects_enabled: routingQuickSelectEnabled,
        quick_selects: routingQuickSelectOptions
      };
    }
    return {};
  };


  const handleAvatarSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !agentId) return;

    setAvatarUploading(true);
    try {
      const res = await uploadAgentAvatar(agentId, file);
      setAvatarUrl(res.data.avatar_url || '');
    } catch (error) {
      console.error('Failed to upload avatar:', error);
    } finally {
      setAvatarUploading(false);
      if (e.target) e.target.value = '';
    }
  };

  const handleRemoveAvatar = async () => {
    if (!agentId || !avatarUrl) return;

    setAvatarUploading(true);
    try {
      await deleteAgentAvatar(agentId);
      setAvatarUrl('');
    } catch (error) {
      console.error('Failed to remove avatar:', error);
    } finally {
      setAvatarUploading(false);
    }
  };

  const handleSaveEditableFiles = async () => {
    if (!agentId) {
      toast.error('Save the agent first before editing its living files.');
      return;
    }
    setSavingEditableFiles(true);
    try {
      const { data } = await updateAgentEditableFiles(agentId, { files: editableFiles });
      setEditableFiles(data.files || editableFiles);
      toast.success('Agent files saved.');
    } catch (error) {
      console.error('Failed to save agent files:', error);
      toast.error(formatApiError(error));
    } finally {
      setSavingEditableFiles(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    // Routing agents can be saved with fewer than 2 links, but remain inactive until fully connected
    if (category === 'routing') {
      const linkedTargets = [...new Set(
        handoffRules
          .map(r => r.target_agent_id)
          .filter(id => id && id.trim())
      )];
      if (linkedTargets.length < 2) {
        setRoutingValidationError('This routing agent can be saved now, but it will remain inactive until at least 2 destination agents are linked in Handoff Rules.');
      } else {
        setRoutingValidationError('');
      }
    }

    const linkedTargets = category === 'routing'
      ? [...new Set(handoffRules.map(r => r.target_agent_id).filter(id => id && id.trim()))]
      : [];

    setSaving(true);
    try {
      const data = {
        name,
        display_name: displayName,
        handoff_style: handoffStyle,
        category,
        description,
        system_prompt: systemPrompt,
        llm_provider: llmProvider,
        llm_model: llmModel,
        use_platform_key: modelAccessMode === 'platform',
        custom_api_key_id: modelAccessMode === 'api_key' ? (customApiKeyId || null) : null,
        model_access_mode: modelAccessMode,
        model_provider_connection_id: modelAccessMode === 'connected_account' ? (modelProviderConnectionId || null) : null,
        fallback_models: fallbackModels,
        fallback_triggers: fallbackTriggers,
        handoff_rules: handoffRules,
        is_active: category === 'routing' && linkedTargets.length < 2 ? false : isActive,
        temperature,
        max_tokens: maxTokens,
        top_p: topP,
        greeting_message: greetingMessage,
        tone,
        response_length: responseLength,
        max_conversation_turns: maxConversationTurns,
        conversation_timeout_minutes: conversationTimeoutMinutes,
        fallback_message: fallbackMessage,
        contact_config: {
          enabled: contactEnabled,
          fields: contactFields,
          required_fields: contactRequired,
          timing: contactTiming,
          widget_mode: contactWidgetMode,
        },
        widget_config: {
          bubble_animation: bubbleAnimation,
          position: widgetPosition,
          primary_color: widgetColor,
          theme_mode: widgetThemeMode,
          advanced_styling: advancedStyling,
          typing_animation: typingAnimation,
          typing_pace: typingPace,
          chunk_responses: chunkResponses,
          ...(advancedStyling ? widgetThemeOverrides : {}),
          show_avatar: showAvatar,
        },
        dynamic_variables: dynamicVariables,
        instruction_config: instructionConfig,
        knowledge_base_config: {
          mode: kbMode,
          max_results: Number(kbMaxResults) || (category === 'routing' ? 2 : category === 'appointment_setting' ? 3 : 5),
          notes: kbNotes,
          shared_group_keys: sharedGroupKeys,
        },
        category_settings: buildCategorySettings(),
        escalation_config: {
          enabled: escalationEnabled,
          mode: escalationMode,
          email_recipients: escalationEmails.split(',').map(v => v.trim()).filter(Boolean),
          webhook_url: escalationWebhookUrl,
          webhook_secret: escalationWebhookSecret,
          internal_note: escalationInternalNote,
        },
      };

      if (isEditing) {
        await updateAgent(agentId, data);
        if (linkedSources.length > 0 || knowledgeSources.length > 0) {
          await axios.put(`${API}/agents/${agentId}/knowledge-sources`, 
            { source_ids: linkedSources }, 
            { withCredentials: true }
          );
        }
        toast.success('Agent saved successfully');
        navigate('/agents');
      } else {
        const res = await createAgent(data);
        if (linkedSources.length > 0) {
          await axios.put(`${API}/agents/${res.data.id}/knowledge-sources`, 
            { source_ids: linkedSources }, 
            { withCredentials: true }
          );
        }
        toast.success('Agent created successfully');
        navigate(`/agents/${res.data.id}`);
        return;
      }
    } catch (e) {
      console.error('Failed to save agent:', e);
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const handleCopyEmbed = () => {
    navigator.clipboard.writeText(embedCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const insertIntoPrompt = (text) => {
    const el = promptRef.current;
    if (el) {
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const newPrompt = systemPrompt.substring(0, start) + text + systemPrompt.substring(end);
      setSystemPrompt(newPrompt);
      setTimeout(() => {
        el.selectionStart = el.selectionEnd = start + text.length;
        el.focus();
      }, 0);
    } else {
      setSystemPrompt(systemPrompt + text);
    }
  };

  const addDynamicVariable = () => {
    if (newVarKey.trim()) {
      setDynamicVariables({ ...dynamicVariables, [newVarKey.trim()]: newVarValue });
      setNewVarKey('');
      setNewVarValue('');
    }
  };

  const removeDynamicVariable = (key) => {
    const updated = { ...dynamicVariables };
    delete updated[key];
    setDynamicVariables(updated);
  };

  const toggleContactField = (field) => {
    if (contactFields.includes(field)) {
      setContactFields(contactFields.filter(f => f !== field));
      setContactRequired(contactRequired.filter(f => f !== field));
    } else {
      setContactFields([...contactFields, field]);
    }
  };

  const toggleContactRequired = (field) => {
    if (contactRequired.includes(field)) {
      setContactRequired(contactRequired.filter(f => f !== field));
    } else {
      if (!contactFields.includes(field)) setContactFields([...contactFields, field]);
      setContactRequired([...contactRequired, field]);
    }
  };

  const addHandoffRule = (type = 'keyword') => {
    if (type === 'keyword') {
      setHandoffRules([...handoffRules, { type: 'keyword', trigger: '', condition: 'contains', target_agent_id: '' }]);
    } else if (type === 'intent') {
      setHandoffRules([...handoffRules, { type: 'intent', intent: '', target_agent_id: '' }]);
    }
  };

  const updateHandoffRule = (index, field, value) => {
    const updated = [...handoffRules];
    updated[index][field] = value;
    setHandoffRules(updated);
  };

  const removeHandoffRule = (index) => {
    setHandoffRules(handoffRules.filter((_, i) => i !== index));
  };

  const availableIntents = [
    { value: 'general_inquiry', label: 'General Inquiry / Information Request', desc: 'User wants general information, has early-stage questions, or is exploring services without a specific transactional request yet' },
    { value: 'purchase_interest', label: 'Purchase Interest', desc: 'User shows buying signals or asks about pricing/purchase' },
    { value: 'scheduling_request', label: 'Scheduling Request', desc: 'User wants to book a meeting or appointment' },
    { value: 'technical_escalation', label: 'Technical Escalation', desc: 'Issue requires specialized technical support' },
    { value: 'billing_issue', label: 'Billing Issue', desc: 'User has billing, payment, or subscription concerns' },
    { value: 'frustration', label: 'Customer Frustration', desc: 'User is frustrated or dissatisfied, needs human touch' },
    { value: 'human_request', label: 'Human Agent Request', desc: 'User explicitly asks to speak with a human' },
    { value: 'upsell_opportunity', label: 'Upsell Opportunity', desc: 'User is interested in upgrading or additional products' },
    { value: 'cancellation_risk', label: 'Cancellation Risk', desc: 'User mentions canceling or leaving' },
  ];

  // Filter out current agent from dropdown
  const targetAgentOptions = orgAgents.filter(a => a.id !== agentId);
  const effectiveAgentName = name || displayName || 'This Agent';
  const getAgentBackendName = (agent) => agent?.name || agent?.display_name_effective || agent?.display_name || 'Agent';
  const getAgentWidgetName = (agent) => agent?.display_name_effective || agent?.display_name || agent?.name || 'Agent';


  const addQualificationQuestion = () => {
    if (newQualQuestion.trim()) {
      setQualificationQuestions([...qualificationQuestions, newQualQuestion.trim()]);
      setNewQualQuestion('');
    }
  };


  const addQuickSelectOption = () => {
    if (newQuickSelectLabel.trim() && newQuickSelectMessage.trim()) {
      setRoutingQuickSelectOptions([...routingQuickSelectOptions, { label: newQuickSelectLabel.trim(), message: newQuickSelectMessage.trim() }]);
      setNewQuickSelectLabel('');
      setNewQuickSelectMessage('');
    }
  };

  const removeQuickSelectOption = (index) => {
    setRoutingQuickSelectOptions(routingQuickSelectOptions.filter((_, i) => i !== index));
  };

  // ==================== RENDER ====================

  if (loading) {
    return (
      <Layout>
        <div className="p-8 flex items-center justify-center min-h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="h-screen flex flex-col" data-testid="agent-builder-page">
        {/* Header */}
        <div className="px-4 py-3 border-b border-border flex items-center justify-between bg-card">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/agents')} className="p-2 hover:bg-secondary rounded-lg transition-colors" data-testid="back-button">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-lg font-semibold font-['Outfit'] text-foreground">
                {isEditing ? 'Edit Agent' : 'Create Agent'}
              </h1>
              {name && <p className="text-xs text-muted-foreground">{name}</p>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isEditing && (
              <Button variant="outline" className="rounded-lg gap-2" asChild data-testid="test-agent-button">
                <Link to={`/agents/${agentId}/proving-grounds`}>
                  <FlaskConical className="w-4 h-4" />
                  Test Agent
                </Link>
              </Button>
            )}
            <div className="flex items-center gap-2 mr-3">
              <span className="text-xs text-muted-foreground">Active</span>
              <Switch checked={isActive} onCheckedChange={setIsActive} data-testid="active-toggle" />
            </div>
            <Button
              onClick={handleSubmit}
              disabled={saving || !name}
              className="rounded-lg gap-2"
              title={category === 'routing' && [...new Set(handoffRules.map(r => r.target_agent_id).filter(id => id && id.trim()))].length < 2 ? 'Routing agents can be saved now, but they will stay inactive until at least 2 destination agents are linked.' : undefined}
              data-testid="save-agent-button"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save Agent
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-hidden bg-card">
          <div className="h-full overflow-y-auto">
            {isEditing ? (
              <div className="mx-5 mt-5 rounded-2xl border border-primary/20 bg-primary/5 p-4 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                    <FlaskConical className="w-4 h-4 text-primary" />
                    Proving Grounds is now separate from the builder
                  </div>
                  <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
                    Keep building in full width here, then jump into Proving Grounds to test conversations, capture feedback, and decide whether the next update belongs in prompts, handoffs, or the knowledge base.
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button className="rounded-lg gap-2" asChild>
                    <Link to={`/agents/${agentId}/proving-grounds`} data-testid="hero-test-agent-button">
                      <FlaskConical className="w-4 h-4" />
                      Test Agent
                    </Link>
                  </Button>
                  <Button variant="outline" className="rounded-lg gap-2" onClick={() => navigate(`/agents/${agentId}/widget`)} data-testid="hero-open-widget-button">
                    <Code2 className="w-4 h-4" />
                    Widget & Embed
                  </Button>
                </div>
              </div>
            ) : (
              <div className="mx-5 mt-5 rounded-2xl border border-dashed border-border bg-secondary/20 p-4">
                <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                  <FlaskConical className="w-4 h-4 text-primary" />
                  Proving Grounds unlocks after the first save
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Save this agent to open a dedicated testing space where you can run conversations and capture structured improvement feedback.
                </p>
              </div>
            )}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full">
              <div className="sticky top-0 z-10 bg-card border-b border-border">
                <TabsList className="w-full justify-start bg-transparent rounded-none p-0 h-auto flex-wrap">
                  {[
                    { value: 'identity', icon: Bot, label: 'Identity' },
                    { value: 'ai', icon: Brain, label: 'AI Config' },
                    { value: 'behavior', icon: Settings2, label: 'Behavior' },
                    { value: 'contact', icon: UserCheck, label: 'Contact Capture' },
                    { value: 'category', icon: Puzzle, label: 'Category' },
                    { value: 'knowledge', icon: Database, label: 'Knowledge' },
                    { value: 'living-files', icon: FileText, label: 'Living Files' },
                    { value: 'handoff', icon: Zap, label: 'Handoff' },
                    { value: 'widget', icon: Code2, label: 'Widget' },
                  ].map(tab => (
                    <TabsTrigger 
                      key={tab.value} 
                      value={tab.value} 
                      className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-3 py-2.5 gap-1.5 text-xs"
                      data-testid={`tab-${tab.value}`}
                    >
                      <tab.icon className="w-3.5 h-3.5" />
                      {tab.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </div>

              {/* ============ IDENTITY & PERSONALITY ============ */}
              <TabsContent value="identity" className="p-5 space-y-6 m-0">
                <SectionHeader icon={Bot} title="Primary Business Agent" description="Most businesses only need one strong agent. Use the builder below to define its role, scope, tone, and behavior." />
                <div className="rounded-xl border border-border bg-secondary/10 p-4">
                  <p className="text-sm font-medium text-foreground">Start with one great agent</p>
                  <p className="text-xs text-muted-foreground mt-1">Scheduling, sales, support, and lead handling can usually live inside a single business agent. Advanced multi-agent routing should be treated as an exception, not the default setup path.</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="name">Internal Name</Label>
                  <p className="text-xs text-muted-foreground">Used for backend/operator workflows and agent management.</p>
                  <Input id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., support-tier-1" className="rounded-lg" data-testid="agent-name-input" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="display-name">Display Name</Label>
                  <p className="text-xs text-muted-foreground">What visitors see in the widget and handoff messaging.</p>
                  <Input id="display-name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="e.g., Alex from Support" className="rounded-lg" data-testid="agent-display-name-input" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea id="description" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Brief description of what this agent does" className="rounded-lg resize-none" rows={2} data-testid="agent-description-input" />
                </div>

                <SectionHeader icon={Puzzle} title="Behavior Builder" description="Define the agent's role, audience, scope, and truth policy using structured settings before adding custom instructions." />

                <div className="space-y-2">
                  <Label htmlFor="primary-role">Primary Role</Label>
                  <Input id="primary-role" value={primaryRole} onChange={(e) => setPrimaryRole(e.target.value)} placeholder="e.g., First-response sales qualifier for demo requests" className="rounded-lg" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="target-audience">Who this agent talks to</Label>
                  <Input id="target-audience" value={targetAudience} onChange={(e) => setTargetAudience(e.target.value)} placeholder="e.g., Small-business owners evaluating our software" className="rounded-lg" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="conversation-purpose">Main conversation purpose</Label>
                  <Textarea id="conversation-purpose" value={conversationPurpose} onChange={(e) => setConversationPurpose(e.target.value)} placeholder="e.g., Help visitors quickly understand fit, answer common pre-sales questions, and move qualified leads toward a demo." className="rounded-lg resize-none" rows={3} />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="approved-topics">Approved Topics</Label>
                    <Textarea id="approved-topics" value={approvedTopicsText} onChange={(e) => setApprovedTopicsText(e.target.value)} onBlur={(e) => { const parsed = parseListText(e.target.value); setApprovedTopics(parsed); setApprovedTopicsText(parsed.join('\n')); }} placeholder="One per line or comma-separated&#10;Pricing&#10;Implementation timelines&#10;Core product fit" className="rounded-lg resize-none" rows={5} />
                    <p className="text-[10px] text-muted-foreground">Recommended: keep this list focused. If it gets too broad, the agent will get harder to control.</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="out-of-scope-topics">Out-of-Scope Topics</Label>
                    <Textarea id="out-of-scope-topics" value={outOfScopeTopicsText} onChange={(e) => setOutOfScopeTopicsText(e.target.value)} onBlur={(e) => { const parsed = parseListText(e.target.value); setOutOfScopeTopics(parsed); setOutOfScopeTopicsText(parsed.join('\n')); }} placeholder="One per line or comma-separated&#10;Legal advice&#10;Custom contract negotiation&#10;Engineering deep-dives" className="rounded-lg resize-none" rows={5} />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>When the agent is unsure</Label>
                  <Select value={unknownAnswerBehavior} onValueChange={(value) => { setBehaviorDefaultsTouched(true); setUnknownAnswerBehavior(value); }}>
                    <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admit_and_offer_help">Admit uncertainty and offer help</SelectItem>
                      <SelectItem value="admit_only">Admit uncertainty plainly</SelectItem>
                      <SelectItem value="stay_narrow_and_redirect">Stay narrow and redirect to a next step</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <BuilderAdvancedSection
                  title="Advanced Style"
                  summary={styleSummary}
                  open={showAdvancedStyle}
                  onOpenChange={setShowAdvancedStyle}
                >
                  <div className="space-y-2">
                    <Label htmlFor="desired-personality">Desired Personality</Label>
                    <Input id="desired-personality" value={desiredPersonality} onChange={(e) => setDesiredPersonality(e.target.value)} placeholder="e.g., Calm, sharp, practical, never pushy" className="rounded-lg" />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Answer style</Label>
                      <Select value={directnessMode} onValueChange={(value) => { setBehaviorDefaultsTouched(true); setDirectnessMode(value); }}>
                        <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="direct">Direct</SelectItem>
                          <SelectItem value="balanced">Balanced</SelectItem>
                          <SelectItem value="consultative">Consultative</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Follow-up question frequency</Label>
                      <Select value={followUpFrequency} onValueChange={(value) => { setBehaviorDefaultsTouched(true); setFollowUpFrequency(value); }}>
                        <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="often">Often</SelectItem>
                          <SelectItem value="rarely">Rarely</SelectItem>
                          <SelectItem value="only_when_necessary">Only when necessary</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
                    <div className="space-y-2">
                      <Label>Max follow-up questions at once</Label>
                      <Select value={String(maxFollowUpQuestions)} onValueChange={(value) => { setBehaviorDefaultsTouched(true); setMaxFollowUpQuestions(Number(value)); }}>
                        <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">1</SelectItem>
                          <SelectItem value="2">2</SelectItem>
                          <SelectItem value="3">3</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Formatting preference</Label>
                      <Select value={formattingPreference} onValueChange={(value) => { setBehaviorDefaultsTouched(true); setFormattingPreference(value); }}>
                        <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="plain_chat">Plain chat replies</SelectItem>
                          <SelectItem value="short_paragraphs">Short paragraphs</SelectItem>
                          <SelectItem value="bullets_when_helpful">Bullets when helpful</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="flex items-center justify-between rounded-xl border border-border p-4 bg-secondary/10">
                    <div>
                      <p className="text-sm font-medium text-foreground">Keep answers short first</p>
                      <p className="text-xs text-muted-foreground mt-1">Start compact and expand only if the user asks for more detail.</p>
                    </div>
                    <Switch checked={expandOnlyIfAsked} onCheckedChange={(value) => { setBehaviorDefaultsTouched(true); setExpandOnlyIfAsked(value); }} />
                  </div>
                </BuilderAdvancedSection>

                <BuilderAdvancedSection
                  title="Advanced Truth & Knowledge"
                  summary={truthSummary}
                  open={showAdvancedTruth}
                  onOpenChange={setShowAdvancedTruth}
                >
                  <div className="space-y-2">
                    <Label>Knowledge strictness</Label>
                    <Select value={knowledgeStrictness} onValueChange={(value) => { setBehaviorDefaultsTouched(true); setKnowledgeStrictness(value); }}>
                      <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="business_only">Only answer from connected business knowledge</SelectItem>
                        <SelectItem value="business_with_light_general">Use business knowledge plus light general explanation</SelectItem>
                        <SelectItem value="strict_no_assumptions">Avoid assumptions entirely and stay narrow</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </BuilderAdvancedSection>

                <BuilderAdvancedSection
                  title="Brand & Examples"
                  summary={brandSummary}
                  open={showBrandExamples}
                  onOpenChange={setShowBrandExamples}
                >
                  <div className="space-y-2">
                    <Label htmlFor="brand-voice-rules">Brand Voice Rules</Label>
                    <Textarea id="brand-voice-rules" value={brandVoiceRules} onChange={(e) => setBrandVoiceRules(e.target.value)} placeholder="Describe how the brand should sound, what it should emphasize, and what it should avoid." className="rounded-lg resize-none" rows={3} />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="forbidden-phrases">Forbidden Phrases</Label>
                    <Textarea id="forbidden-phrases" value={forbiddenPhrasesText} onChange={(e) => setForbiddenPhrasesText(e.target.value)} onBlur={(e) => { const parsed = parseListText(e.target.value); setForbiddenPhrases(parsed); setForbiddenPhrasesText(parsed.join('\n')); }} placeholder="One per line or comma-separated&#10;As an AI language model&#10;I can't guarantee that&#10;Revolutionary" className="rounded-lg resize-none" rows={4} />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="example-good-responses">Example Good Responses</Label>
                      <Textarea id="example-good-responses" value={exampleGoodResponses.join('\n---\n')} onChange={(e) => setExampleGoodResponses(e.target.value.split(/\n---\n/).map(v => v.trim()).filter(Boolean))} placeholder={"Separate examples with ---\n\nYes — based on what you've told me, it sounds like the Pro plan is the best fit. Want me to walk you through the pricing differences?\n---\nI don't have enough information to answer that confidently. I can point you to our support team or help narrow the question."} className="rounded-lg resize-none" rows={7} />
                      <p className="text-[10px] text-muted-foreground">Show the builder what good sounds like. Separate multiple examples with <code>---</code>.</p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="example-bad-responses">Example Bad Responses</Label>
                      <Textarea id="example-bad-responses" value={exampleBadResponses.join('\n---\n')} onChange={(e) => setExampleBadResponses(e.target.value.split(/\n---\n/).map(v => v.trim()).filter(Boolean))} placeholder={"Separate examples with ---\n\nAs an AI language model, I cannot provide that information.\n---\nOur revolutionary platform can solve literally every business problem you have."} className="rounded-lg resize-none" rows={7} />
                      <p className="text-[10px] text-muted-foreground">Capture patterns the agent should avoid — tone misses, bad claims, weak fallback behavior, or off-brand wording.</p>
                    </div>
                  </div>
                </BuilderAdvancedSection>

                <div className="space-y-2">
                  <Label htmlFor="greeting">Welcome Message</Label>
                  <p className="text-xs text-muted-foreground">First message the agent sends when a conversation starts</p>
                  <Textarea id="greeting" value={greetingMessage} onChange={(e) => setGreetingMessage(e.target.value)} placeholder="e.g., Hi! Welcome to Acme Corp. How can I help you today?" className="rounded-lg resize-none" rows={2} data-testid="greeting-input" />
                </div>

                <SectionHeader icon={MessageSquare} title="Personality" description="Control how the agent communicates" />

                <div className="space-y-2">
                  <Label>Tone</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {tones.map(t => (
                      <button
                        key={t.value}
                        type="button"
                        onClick={() => setTone(t.value)}
                        className={`p-3 border rounded-lg text-left transition-all ${
                          tone === t.value ? 'border-primary bg-primary/5' : 'border-border hover:border-foreground/20'
                        }`}
                        data-testid={`tone-${t.value}`}
                      >
                        <p className="font-medium text-xs text-foreground">{t.label}</p>
                        <p className="text-[10px] text-muted-foreground">{t.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Response Length</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {['concise', 'balanced', 'detailed'].map(len => (
                      <button
                        key={len}
                        type="button"
                        onClick={() => setResponseLength(len)}
                        className={`p-2.5 border rounded-lg text-center transition-all capitalize text-xs ${
                          responseLength === len ? 'border-primary bg-primary/5 font-medium' : 'border-border hover:border-foreground/20'
                        }`}
                        data-testid={`response-length-${len}`}
                      >
                        {len}
                      </button>
                    ))}
                  </div>
                </div>

                <SectionHeader icon={Zap} title="Custom Instructions" description="Use this for nuance, edge cases, and operator judgment. Apollo already injects many runtime behavior rules from the rest of the builder." />
                <div className="rounded-xl border border-border bg-secondary/20 p-4 space-y-3">
                  <div>
                    <p className="font-medium text-sm text-foreground">How to use this section</p>
                    <p className="text-xs text-muted-foreground mt-1">Treat this as the flexible layer, not the whole brain. Put special cases, brand nuance, or exceptions here rather than re-stating every builder setting manually.</p>
                  </div>
                  <ul className="text-xs text-muted-foreground space-y-1 list-disc pl-4">
                    <li>Good for edge cases, subtle tone guidance, and business-specific exceptions.</li>
                    <li>Avoid repeating tone, response-length, contact-capture, or category rules that are already configured elsewhere.</li>
                    <li>Use dynamic variables when you want reusable business information inserted into custom instructions.</li>
                  </ul>
                </div>
                {Object.keys(dynamicVariables).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    <span className="text-xs text-muted-foreground mr-1">Insert variable:</span>
                    {Object.keys(dynamicVariables).map(k => (
                      <VariableTag key={k} name={k} onInsert={insertIntoPrompt} />
                    ))}
                  </div>
                )}
                <Textarea
                  ref={promptRef}
                  value={systemPrompt}
                  onChange={(e) => setSystemPrompt(e.target.value)}
                  placeholder="Example: If the user asks about implementation timelines, give a practical range and offer the next best step."
                  className="rounded-lg resize-none font-mono text-xs"
                  rows={8}
                  data-testid="system-prompt-input"
                />
                <p className="text-[10px] text-muted-foreground">
                  Use {'{{variable_name}}'} to reference dynamic variables. This field now supplements the builder configuration instead of carrying the full instruction burden by itself.
                </p>

                <div className="border-t border-border pt-5 space-y-4">
                  <BuilderAdvancedSection
                    title="Diagnostics & Preview"
                    summary={`Scope ${builderDiagnostics.scopeScore}/100, Clarity ${builderDiagnostics.clarityScore}/100, ${builderDiagnostics.warnings.length} warning${builderDiagnostics.warnings.length === 1 ? '' : 's'}`}
                    open={showDiagnosticsPreview}
                    onOpenChange={setShowDiagnosticsPreview}
                  >
                    <SectionHeader icon={AlertTriangle} title="Builder Diagnostics" description="Warnings and contradictions based on the current configuration." />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="rounded-xl border border-border bg-secondary/20 p-4">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-foreground">Scope score</span>
                          <span className="text-sm font-mono">{builderDiagnostics.scopeScore}/100</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">Measures how focused or overly broad the topic boundaries look right now.</p>
                      </div>
                      <div className="rounded-xl border border-border bg-secondary/20 p-4">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-foreground">Clarity score</span>
                          <span className="text-sm font-mono">{builderDiagnostics.clarityScore}/100</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">Flags vague role/purpose definition and missing instruction detail.</p>
                      </div>
                    </div>
                    {builderDiagnostics.warnings.length > 0 && (
                      <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4">
                        <p className="text-sm font-medium text-foreground">Warnings</p>
                        <ul className="mt-2 space-y-1 text-xs text-muted-foreground list-disc pl-4">
                          {builderDiagnostics.warnings.map((warning) => (
                            <li key={warning}>{warning}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {builderDiagnostics.conflicts.length > 0 && (
                      <div className="rounded-xl border border-destructive/40 bg-destructive/10 p-4">
                        <p className="text-sm font-medium text-foreground">Conflicts detected</p>
                        <ul className="mt-2 space-y-1 text-xs text-muted-foreground list-disc pl-4">
                          {builderDiagnostics.conflicts.map((conflict) => (
                            <li key={conflict}>{conflict}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <SectionHeader icon={Brain} title="Generated Prompt Preview" description="A readable preview of the builder-generated instruction stack before Apollo adds deeper runtime context." />
                    <div className="rounded-xl border border-border bg-secondary/20 p-4 space-y-2">
                      <p className="text-xs text-muted-foreground">This preview is an operator-facing approximation of what Apollo will compile from the current builder settings. It mirrors the runtime stack more closely now, but it is still a readable preview rather than a byte-for-byte backend prompt dump.</p>
                      <pre className="bg-background rounded-lg p-3 text-xs font-mono overflow-x-auto whitespace-pre-wrap break-words text-foreground">{generatedPromptPreview}</pre>
                    </div>
                  </BuilderAdvancedSection>

                  {isEditing && (
                    <BuilderAdvancedSection
                      title="Prompt Refiner"
                      summary={`${promptImprovements.length} refinement item${promptImprovements.length === 1 ? '' : 's'} pending or recorded for this agent`}
                      open={showPromptRefiner}
                      onOpenChange={setShowPromptRefiner}
                    >
                      <SectionHeader icon={FlaskConical} title="Prompt Refiner" description="Reviewed prompt-improvement items from Proving Grounds for this agent." />
                      {promptImprovements.length === 0 ? (
                        <div className="rounded-xl border border-border bg-secondary/20 p-4">
                          <p className="text-sm font-medium text-foreground">No prompt improvements yet</p>
                          <p className="text-xs text-muted-foreground mt-1">Run test conversations in Proving Grounds, review the feedback, and route the strongest items into prompt improvements.</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {promptImprovements.map((item) => (
                            <div key={item.id} className="rounded-xl border border-border bg-secondary/20 p-4 space-y-3">
                              <div className="flex items-start justify-between gap-3">
                                <div>
                                  <p className="text-sm font-medium text-foreground">{item.title}</p>
                                  <p className="text-xs text-muted-foreground mt-1">{item.summary}</p>
                                </div>
                                <Badge variant="outline">{item.status || 'proposed'}</Badge>
                              </div>
                              {item.suggested_prompt_patch && (
                                <div className="rounded-lg bg-background border border-border p-3">
                                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-2">Suggested prompt patch</p>
                                  <pre className="text-xs font-mono whitespace-pre-wrap break-words text-foreground">{item.suggested_prompt_patch}</pre>
                                </div>
                              )}
                              <div className="flex flex-wrap gap-2">
                                <Button type="button" variant="default" size="sm" disabled={promptImprovementBusyId === item.id || item.status === 'applied'} onClick={() => applyPromptImprovement(item)}>
                                  {promptImprovementBusyId === item.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}Apply to custom instructions
                                </Button>
                                <Button type="button" variant="outline" size="sm" disabled={promptImprovementBusyId === item.id || item.status === 'dismissed'} onClick={() => dismissPromptImprovement(item)}>
                                  Dismiss
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </BuilderAdvancedSection>
                  )}
                </div>
              </TabsContent>

              {/* ============ AI CONFIGURATION ============ */}
              <TabsContent value="ai" className="p-5 space-y-6 m-0">
                <SectionHeader icon={Brain} title="Language Model" description="Choose which AI model powers this agent" />

                <div className="space-y-2">
                  <Label>Provider</Label>
                  <Select value={llmProvider} onValueChange={setLlmProvider}>
                    <SelectTrigger className="rounded-lg" data-testid="provider-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {providers.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Model</Label>
                  <Select value={llmModel} onValueChange={setLlmModel}>
                    <SelectTrigger className="rounded-lg" data-testid="model-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(modelsByProvider[llmProvider] || []).map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className="border-t border-border pt-5 space-y-5">
                  <SectionHeader icon={Settings2} title="Model Parameters" description="Fine-tune how the AI generates responses" />

                  <SliderField
                    label="Temperature"
                    description="Higher = more creative, Lower = more focused"
                    value={temperature}
                    onChange={setTemperature}
                    min={0} max={2} step={0.05}
                    displayValue={temperature.toFixed(2)}
                  />

                  <SliderField
                    label="Max Tokens"
                    description="Maximum length of each response"
                    value={maxTokens}
                    onChange={setMaxTokens}
                    min={64} max={4096} step={64}
                    displayValue={maxTokens.toLocaleString()}
                  />

                  <SliderField
                    label="Top-P (Nucleus Sampling)"
                    description="Controls diversity of token selection"
                    value={topP}
                    onChange={setTopP}
                    min={0} max={1} step={0.05}
                    displayValue={topP.toFixed(2)}
                  />
                </div>

                <div className="border-t border-border pt-5 space-y-4">
                  <SectionHeader icon={Settings2} title="Model Access Policy" description="The user decides which source powers this agent and which fallbacks are allowed" />

                  <div className="space-y-2">
                    <Label>Primary Access Mode</Label>
                    <Select value={modelAccessMode} onValueChange={setModelAccessMode}>
                      <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="platform">Apollo Platform Key</SelectItem>
                        <SelectItem value="api_key">My API Key</SelectItem>
                        <SelectItem value="connected_account">Connected OpenAI Account</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">This is user-controlled policy. Agents consume it but do not choose it.</p>
                  </div>

                  {modelAccessMode === 'api_key' && (
                    <div className="space-y-2">
                      <Label>Your API Key</Label>
                      {apiKeys.length === 0 ? (
                        <p className="text-xs text-muted-foreground">
                          No API keys configured. <a href="/api-keys" className="text-primary hover:underline">Add one</a>
                        </p>
                      ) : providerApiKeys.length === 0 ? (
                        <p className="text-xs text-muted-foreground">
                          No {providers.find(p => p.value === llmProvider)?.label || llmProvider} keys configured. Add one on the <a href="/api-keys" className="text-primary hover:underline">API Keys</a> page or choose another access mode.
                        </p>
                      ) : (
                        <Select value={customApiKeyId} onValueChange={setCustomApiKeyId}>
                          <SelectTrigger className="rounded-lg" data-testid="api-key-select"><SelectValue placeholder="Select an API key" /></SelectTrigger>
                          <SelectContent>
                            {providerApiKeys.map(k => (
                              <SelectItem key={k.id} value={k.id}>{k.name} ({k.key_preview})</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  )}

                  {modelAccessMode === 'connected_account' && (
                    <div className="space-y-2">
                      <Label>Connected OpenAI Account</Label>
                      {openAIConnectedAccountConnections.length === 0 ? (
                        <p className="text-xs text-muted-foreground">
                          No saved OpenAI connected-account token yet. Add one in <a href="/settings" className="text-primary hover:underline">Settings → Integrations</a>.
                        </p>
                      ) : (
                        <Select value={modelProviderConnectionId} onValueChange={setModelProviderConnectionId}>
                          <SelectTrigger className="rounded-lg"><SelectValue placeholder="Select a connected OpenAI account" /></SelectTrigger>
                          <SelectContent>
                            {openAIConnectedAccountConnections.map((connection) => (
                              <SelectItem key={connection.id} value={connection.id}>{connection.name}{connection.account_identifier ? ` (${connection.account_identifier})` : ''}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  )}

                  <div className="space-y-3 rounded-lg border border-border p-4">
                    <div>
                      <Label>Fallback Triggers</Label>
                      <p className="text-xs text-muted-foreground">When these happen, Apollo may move to the configured fallback chain.</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {['rate_limit', 'timeout', 'provider_error', 'model_unavailable'].map((trigger) => {
                        const selected = fallbackTriggers.includes(trigger);
                        return (
                          <Button
                            key={trigger}
                            type="button"
                            variant={selected ? 'default' : 'outline'}
                            className="rounded-lg"
                            onClick={() => setFallbackTriggers((prev) => selected ? prev.filter((item) => item !== trigger) : [...prev, trigger])}
                          >
                            {trigger}
                          </Button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="space-y-3 rounded-lg border border-border p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label>Fallback Models</Label>
                        <p className="text-xs text-muted-foreground">User-defined fallback chain for this agent.</p>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        className="rounded-lg"
                        onClick={() => setFallbackModels((prev) => [...prev, { provider: llmProvider, model: llmModel, access_mode: modelAccessMode }])}
                      >
                        Add Fallback
                      </Button>
                    </div>
                    {fallbackModels.length === 0 ? (
                      <p className="text-xs text-muted-foreground">No fallback models configured yet.</p>
                    ) : fallbackModels.map((fallback, index) => (
                      <div key={`${fallback.provider}-${fallback.model}-${index}`} className="grid md:grid-cols-4 gap-3 items-end">
                        <div className="space-y-2">
                          <Label>Provider</Label>
                          <Select value={fallback.provider} onValueChange={(value) => setFallbackModels((prev) => prev.map((item, i) => i === index ? { ...item, provider: value } : item))}>
                            <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {providers.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Model</Label>
                          <Input value={fallback.model || ''} onChange={(e) => setFallbackModels((prev) => prev.map((item, i) => i === index ? { ...item, model: e.target.value } : item))} className="rounded-lg" />
                        </div>
                        <div className="space-y-2">
                          <Label>Access Mode</Label>
                          <Select value={fallback.access_mode || 'platform'} onValueChange={(value) => setFallbackModels((prev) => prev.map((item, i) => i === index ? { ...item, access_mode: value } : item))}>
                            <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="platform">Platform</SelectItem>
                              <SelectItem value="api_key">API Key</SelectItem>
                              <SelectItem value="connected_account">Connected Account</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <Button type="button" variant="ghost" className="rounded-lg" onClick={() => setFallbackModels((prev) => prev.filter((_, i) => i !== index))}>Remove</Button>
                      </div>
                    ))}
                  </div>
                </div>
              </TabsContent>

              {/* ============ BEHAVIOR ============ */}
              <TabsContent value="behavior" className="p-5 space-y-6 m-0">
                <SectionHeader icon={Settings2} title="Conversation Limits" description="Control conversation length and timeouts" />

                <SliderField
                  label="Max Conversation Turns"
                  description="Maximum back-and-forth exchanges before auto-closing"
                  value={maxConversationTurns}
                  onChange={setMaxConversationTurns}
                  min={5} max={200} step={5}
                />

                <SliderField
                  label="Conversation Timeout (minutes)"
                  description="Close idle conversations after this duration"
                  value={conversationTimeoutMinutes}
                  onChange={setConversationTimeoutMinutes}
                  min={5} max={120} step={5}
                />

                <div className="border-t border-border pt-5">
                  <SectionHeader icon={Shield} title="Fallback Message" description="Sent when the agent can't help or hits its limit" />
                  <Textarea
                    value={fallbackMessage}
                    onChange={(e) => setFallbackMessage(e.target.value)}
                    className="rounded-lg resize-none text-sm"
                    rows={3}
                    data-testid="fallback-message-input"
                  />
                </div>

                <div className="border-t border-border pt-5">
                  <SectionHeader icon={Globe} title="Dynamic Variables" description="Business info that the agent can reference in conversations" />
                  
                  {Object.entries(dynamicVariables).length > 0 && (
                    <div className="space-y-2 mb-4">
                      {Object.entries(dynamicVariables).map(([key, value]) => (
                        <div key={key} className="flex items-center gap-2 bg-secondary/50 rounded-lg p-2.5">
                          <code className="text-xs font-mono text-primary bg-primary/10 px-2 py-0.5 rounded">{'{{' + key + '}}'}</code>
                          <span className="text-xs text-foreground flex-1 truncate">{value}</span>
                          <Button variant="ghost" size="sm" onClick={() => removeDynamicVariable(key)} className="h-6 w-6 p-0 text-destructive hover:text-destructive">
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div className="flex gap-2">
                    <Input value={newVarKey} onChange={(e) => setNewVarKey(e.target.value)} placeholder="Variable name" className="rounded-lg text-sm flex-1" data-testid="new-var-key" />
                    <Input value={newVarValue} onChange={(e) => setNewVarValue(e.target.value)} placeholder="Value" className="rounded-lg text-sm flex-[2]" data-testid="new-var-value" />
                    <Button variant="outline" onClick={addDynamicVariable} disabled={!newVarKey.trim()} className="rounded-lg" data-testid="add-variable-btn">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-2">
                    Common variables: business_name, business_hours, website, phone, address, pricing_url
                  </p>
                </div>
              </TabsContent>

              {/* ============ CONTACT CAPTURE ============ */}
              <TabsContent value="contact" className="p-5 space-y-6 m-0">
                <SectionHeader icon={UserCheck} title="Contact Capture" description="Collect visitor contact details during conversations" />

                <div className="flex items-center justify-between">
                  <div>
                    <Label>Enable Contact Capture</Label>
                    <p className="text-xs text-muted-foreground">Agent will request contact information from visitors</p>
                  </div>
                  <Switch checked={contactEnabled} onCheckedChange={setContactEnabled} data-testid="contact-enabled-toggle" />
                </div>

                {contactEnabled && (
                  <>
                    <div className="space-y-3">
                      <Label>Fields to Collect</Label>
                      {allContactFields.map(f => (
                        <div key={f.key} className="flex items-center justify-between border border-border rounded-lg p-3">
                          <div className="flex items-center gap-3">
                            <Checkbox
                              checked={contactFields.includes(f.key)}
                              onCheckedChange={() => toggleContactField(f.key)}
                              data-testid={`contact-field-${f.key}`}
                            />
                            <f.icon className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm text-foreground">{f.label}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">Required</span>
                            <Switch
                              checked={contactRequired.includes(f.key)}
                              onCheckedChange={() => toggleContactRequired(f.key)}
                              disabled={!contactFields.includes(f.key)}
                              data-testid={`contact-required-${f.key}`}
                            />
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="space-y-2">
                      <Label>Web Widget Behavior</Label>
                      <p className="text-xs text-muted-foreground">Only applies to the website widget. Email/SMS continue using the in-conversation timing below.</p>
                      <Select value={contactWidgetMode} onValueChange={setContactWidgetMode}>
                        <SelectTrigger className="rounded-lg" data-testid="contact-widget-mode-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="in_conversation">Ask inside the chat</SelectItem>
                          <SelectItem value="pre_chat">Show pre-chat form before chat starts</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>In-Conversation Timing</Label>
                      <p className="text-xs text-muted-foreground">Used when contact details are gathered during the chat, and for non-widget channels.</p>
                      <Select value={contactTiming} onValueChange={setContactTiming}>
                        <SelectTrigger className="rounded-lg" data-testid="contact-timing-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="start">At the start of conversation</SelectItem>
                          <SelectItem value="natural">Naturally during conversation</SelectItem>
                          <SelectItem value="before_escalation">Before escalating to human</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}
              </TabsContent>

              {/* ============ CATEGORY-SPECIFIC SETTINGS ============ */}
              <TabsContent value="category" className="p-5 space-y-6 m-0">
                {category === 'routing' && (
                  <>
                    <SectionHeader icon={GitBranch} title="Routing Agent Settings" description="Configure triage behavior and optional quick-select buttons for visitors" />

                    <div className="rounded-xl border border-blue-200 bg-blue-50 dark:bg-blue-950/20 dark:border-blue-800 p-4 text-sm text-blue-800 dark:text-blue-300 space-y-1">
                      <p className="font-semibold flex items-center gap-2"><GitBranch className="w-4 h-4" /> Routing Agent rules</p>
                      <p>This agent is a front-door triage layer. It must be linked to <strong>at least 2 destination agents</strong> via Handoff Rules — it will not route properly otherwise.</p>
                      <p>Configure the Handoff Rules tab to define which agents this router can send visitors to.</p>
                    </div>

                    {routingValidationError && (
                      <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 flex items-start gap-2 text-sm text-destructive">
                        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        {routingValidationError}
                      </div>
                    )}

                    <div className="border-t border-border pt-5">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h4 className="font-semibold text-sm text-foreground">Quick-Select Buttons</h4>
                          <p className="text-xs text-muted-foreground mt-0.5">Show clickable intent buttons in the widget to help visitors self-select their route</p>
                        </div>
                        <Switch checked={routingQuickSelectEnabled} onCheckedChange={setRoutingQuickSelectEnabled} data-testid="quick-select-enabled-toggle" />
                      </div>

                      {routingQuickSelectEnabled && (
                        <>
                          {routingQuickSelectOptions.length > 0 && (
                            <div className="space-y-2 mb-4">
                              {routingQuickSelectOptions.map((opt, i) => (
                                <div key={i} className="flex items-center gap-2 bg-secondary/50 rounded-lg p-2.5">
                                  <Badge variant="outline" className="h-5 w-5 flex items-center justify-center p-0 text-[10px] flex-shrink-0">{i + 1}</Badge>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs font-medium text-foreground truncate">{opt.label}</p>
                                    <p className="text-[10px] text-muted-foreground truncate">{opt.message}</p>
                                  </div>
                                  <Button variant="ghost" size="sm" onClick={() => removeQuickSelectOption(i)} className="h-6 w-6 p-0 text-destructive hover:text-destructive flex-shrink-0">
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}

                          <div className="space-y-2">
                            <div className="flex gap-2">
                              <Input
                                value={newQuickSelectLabel}
                                onChange={(e) => setNewQuickSelectLabel(e.target.value)}
                                placeholder="Button label (e.g., Book a demo)"
                                className="rounded-lg text-sm flex-1"
                                data-testid="quick-select-label-input"
                              />
                            </div>
                            <div className="flex gap-2">
                              <Input
                                value={newQuickSelectMessage}
                                onChange={(e) => setNewQuickSelectMessage(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && addQuickSelectOption()}
                                placeholder="Message sent when clicked (e.g., I'd like to book a demo)"
                                className="rounded-lg text-sm flex-1"
                                data-testid="quick-select-message-input"
                              />
                              <Button
                                variant="outline"
                                onClick={addQuickSelectOption}
                                disabled={!newQuickSelectLabel.trim() || !newQuickSelectMessage.trim()}
                                className="rounded-lg"
                                data-testid="add-quick-select-btn"
                              >
                                <Plus className="w-4 h-4" />
                              </Button>
                            </div>
                            <p className="text-[10px] text-muted-foreground">
                              The label appears as the button text; the message is sent automatically when clicked.
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                  </>
                )}

                {category === 'customer_service' && (
                  <>
                    <SectionHeader icon={Headphones} title="Customer Service Settings" description="Configure support-specific behavior" />
                    
                    <SliderField
                      label="Escalation Threshold"
                      description="Number of exchanges before offering human handoff"
                      value={escalationThreshold}
                      onChange={setEscalationThreshold}
                      min={1} max={20} step={1}
                    />

                    <div className="border-t border-border pt-5">
                      <SectionHeader icon={Clock} title="Business Hours" description="Let the agent know when your team is available" />
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <Label className="text-xs">Start Time</Label>
                          <Input type="time" value={businessHoursStart} onChange={(e) => setBusinessHoursStart(e.target.value)} className="rounded-lg" data-testid="business-hours-start" />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs">End Time</Label>
                          <Input type="time" value={businessHoursEnd} onChange={(e) => setBusinessHoursEnd(e.target.value)} className="rounded-lg" data-testid="business-hours-end" />
                        </div>
                      </div>
                      <div className="space-y-1.5 mt-3">
                        <Label className="text-xs">Timezone</Label>
                        <Select value={businessTimezone} onValueChange={setBusinessTimezone}>
                          <SelectTrigger className="rounded-lg" data-testid="business-timezone-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {timezones.map(tz => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="border-t border-border pt-5 space-y-4">
                      <SectionHeader icon={AlertTriangle} title="Escalation Destinations" description="Choose what Apollo should do when support needs human follow-up" />
                      <div className="flex items-center justify-between rounded-lg border border-border p-3">
                        <div>
                          <Label>Enable escalation delivery</Label>
                          <p className="text-xs text-muted-foreground">Apollo logs every escalation event and can send it to a practical destination.</p>
                        </div>
                        <Switch checked={escalationEnabled} onCheckedChange={setEscalationEnabled} data-testid="escalation-enabled-toggle" />
                      </div>

                      <div className="space-y-2">
                        <Label>Destination type</Label>
                        <Select value={escalationMode} onValueChange={setEscalationMode}>
                          <SelectTrigger className="rounded-lg" data-testid="escalation-mode-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No delivery</SelectItem>
                            <SelectItem value="internal_inbox">Internal inbox</SelectItem>
                            <SelectItem value="email">Email</SelectItem>
                            <SelectItem value="webhook">Webhook</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {escalationMode === 'email' && (
                        <div className="space-y-2">
                          <Label>Email recipients</Label>
                          <Input value={escalationEmails} onChange={(e) => setEscalationEmails(e.target.value)} placeholder="support@company.com, manager@company.com" className="rounded-lg" data-testid="escalation-emails-input" />
                        </div>
                      )}

                      {escalationMode === 'webhook' && (
                        <>
                          <div className="space-y-2">
                            <Label>Webhook URL</Label>
                            <Input value={escalationWebhookUrl} onChange={(e) => setEscalationWebhookUrl(e.target.value)} placeholder="https://example.com/apollo/escalations" className="rounded-lg" data-testid="escalation-webhook-url" />
                          </div>
                          <div className="space-y-2">
                            <Label>Webhook secret</Label>
                            <Input value={escalationWebhookSecret} onChange={(e) => setEscalationWebhookSecret(e.target.value)} placeholder="Optional signing secret" className="rounded-lg" data-testid="escalation-webhook-secret" />
                          </div>
                        </>
                      )}

                      {escalationMode === 'internal_inbox' && (
                        <div className="space-y-2">
                          <Label>Internal note</Label>
                          <Textarea value={escalationInternalNote} onChange={(e) => setEscalationInternalNote(e.target.value)} placeholder="Optional note for your human team" className="rounded-lg resize-none" rows={3} data-testid="escalation-internal-note" />
                        </div>
                      )}
                    </div>
                  </>
                )}

                {category === 'appointment_setting' && (
                  <>
                    <SectionHeader icon={Calendar} title="Appointment Settings" description="Configure scheduling behavior" />

                    <div className="border rounded-xl p-4 bg-muted/20 space-y-3">
                      <div className="flex items-center justify-between gap-4">
                        <div>
                          <Label>Use organization scheduling defaults</Label>
                          <p className="text-xs text-muted-foreground">Manage shared availability, provider, and booking rules in Settings → Scheduling. Turn this off only when this agent needs custom scheduling behavior.</p>
                        </div>
                        <Switch checked={useOrganizationSchedulingDefaults} onCheckedChange={setUseOrganizationSchedulingDefaults} />
                      </div>
                      {useOrganizationSchedulingDefaults && (
                        <p className="text-xs text-muted-foreground">This agent is currently inheriting shared scheduling defaults from Settings. Agent-specific confirmation text still stays here.</p>
                      )}
                    </div>

                    <div className="space-y-1.5">
                      <Label>Timezone</Label>
                      <Select value={appointmentTimezone} onValueChange={setAppointmentTimezone} disabled={useOrganizationSchedulingDefaults}>
                        <SelectTrigger className="rounded-lg" data-testid="appointment-timezone-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {timezones.map(tz => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <Label>Scheduling Provider</Label>
                        <Select value={schedulingProvider} onValueChange={setSchedulingProvider} disabled={useOrganizationSchedulingDefaults}>
                          <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="booking_link">Booking Link Only</SelectItem>
                            <SelectItem value="google_calendar">Google Calendar</SelectItem>
                            <SelectItem value="apple_calendar">Apple Calendar</SelectItem>
                            <SelectItem value="calendly">Calendly</SelectItem>
                            <SelectItem value="highlevel">HighLevel</SelectItem>
                            <SelectItem value="cal_com">Cal.com</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5">
                        <Label>Booking Mode</Label>
                        <Select value={bookingMode} onValueChange={setBookingMode} disabled={useOrganizationSchedulingDefaults}>
                          <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="link_only">Share Booking Link</SelectItem>
                            <SelectItem value="availability_lookup">Show Live Availability</SelectItem>
                            <SelectItem value="direct_booking">Book Directly In Chat</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <Label>Provider Connection</Label>
                        <Select value={providerConnectionId || '__none__'} onValueChange={(value) => { setProviderConnectionId(value === '__none__' ? '' : value); setAvailabilityPreview(null); }} disabled={useOrganizationSchedulingDefaults}>
                          <SelectTrigger className="rounded-lg"><SelectValue placeholder="Select a saved provider connection" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">No saved connection</SelectItem>
                            {providerConnections.filter(c => c.provider === schedulingProvider).map(c => (
                              <SelectItem key={c.id} value={c.id}>{c.name}{c.account_identifier ? ` (${c.account_identifier})` : ''}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {schedulingProvider === 'google_calendar' && (
                          <p className="mt-2 text-xs text-muted-foreground">Connect Google Calendar in General Settings / Integrations, then select it here.</p>
                        )}
                      </div>
                      <div className="space-y-1.5">
                        <Label>Event Type / Calendar ID</Label>
                        <Input value={eventTypeId} onChange={(e) => setEventTypeId(e.target.value)} placeholder="event type id, calendar id, or scheduler ref" className="rounded-lg" disabled={useOrganizationSchedulingDefaults} />
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <Label>Booking URL</Label>
                      <p className="text-xs text-muted-foreground">Fallback or provider booking link (Calendly, Cal.com, HighLevel, etc.)</p>
                      <Input value={bookingUrl} onChange={(e) => setBookingUrl(e.target.value)} placeholder="https://calendly.com/your-link" className="rounded-lg" data-testid="booking-url-input" disabled={useOrganizationSchedulingDefaults} />
                    </div>

                    <div className="border rounded-xl p-4 space-y-4 bg-muted/20">
                      <div className="flex items-center justify-between gap-4">
                        <div>
                          <Label>Availability Rules</Label>
                          <p className="text-xs text-muted-foreground">Use the same richer schedule model as org defaults: multiple blocks per day and date-specific overrides.</p>
                        </div>
                        <Switch checked={businessHoursEnabled} onCheckedChange={setBusinessHoursEnabled} disabled={useOrganizationSchedulingDefaults} />
                      </div>

                      <div className="space-y-3">
                        {WEEKDAY_LABELS.map(([dayKey, dayLabel]) => {
                          const day = weeklyAvailability?.[dayKey] || { enabled: false, ranges: [{ start: '09:00', end: '17:00' }] };
                          return (
                            <div key={dayKey} className="border rounded-lg p-3 bg-background/60 space-y-3">
                              <div className="flex items-center justify-between gap-3">
                                <div className="font-medium text-sm">{dayLabel}</div>
                                <div className="flex items-center gap-2">
                                  <Switch
                                    checked={!!day.enabled}
                                    disabled={useOrganizationSchedulingDefaults}
                                    onCheckedChange={(value) => setWeeklyAvailability({
                                      ...(weeklyAvailability || {}),
                                      [dayKey]: { ...day, enabled: value },
                                    })}
                                  />
                                  <span className="text-xs text-muted-foreground">{day.enabled ? 'On' : 'Off'}</span>
                                </div>
                              </div>
                              {(day.ranges || []).map((range, rangeIndex) => (
                                <div key={`${dayKey}-${rangeIndex}`} className="grid grid-cols-1 md:grid-cols-[1fr,1fr,auto] gap-3 items-end">
                                  <div className="space-y-1.5">
                                    <Label>Start</Label>
                                    <Input type="time" value={range.start || '09:00'} disabled={useOrganizationSchedulingDefaults || !day.enabled} onChange={(e) => setWeeklyAvailability({
                                      ...(weeklyAvailability || {}),
                                      [dayKey]: { ...day, ranges: (day.ranges || []).map((r, i) => i === rangeIndex ? { ...r, start: e.target.value } : r) }
                                    })} className="rounded-lg" />
                                  </div>
                                  <div className="space-y-1.5">
                                    <Label>End</Label>
                                    <Input type="time" value={range.end || '17:00'} disabled={useOrganizationSchedulingDefaults || !day.enabled} onChange={(e) => setWeeklyAvailability({
                                      ...(weeklyAvailability || {}),
                                      [dayKey]: { ...day, ranges: (day.ranges || []).map((r, i) => i === rangeIndex ? { ...r, end: e.target.value } : r) }
                                    })} className="rounded-lg" />
                                  </div>
                                  <Button type="button" variant="ghost" size="icon" disabled={useOrganizationSchedulingDefaults || !day.enabled || (day.ranges || []).length <= 1} onClick={() => setWeeklyAvailability({
                                    ...(weeklyAvailability || {}),
                                    [dayKey]: { ...day, ranges: (day.ranges || []).filter((_, i) => i !== rangeIndex) }
                                  })}>
                                    <X className="w-4 h-4" />
                                  </Button>
                                </div>
                              ))}
                              <Button type="button" variant="outline" size="sm" disabled={useOrganizationSchedulingDefaults || !day.enabled} onClick={() => setWeeklyAvailability({
                                ...(weeklyAvailability || {}),
                                [dayKey]: { ...day, ranges: [...(day.ranges || []), { start: '09:00', end: '17:00' }] }
                              })} className="rounded-lg gap-2">
                                <Plus className="w-4 h-4" /> Add time block
                              </Button>
                            </div>
                          );
                        })}

                        <div className="border rounded-lg p-3 bg-background/60 space-y-3">
                          <div className="flex items-center justify-between gap-3">
                            <div>
                              <div className="font-medium text-sm">Date Overrides</div>
                              <p className="text-xs text-muted-foreground">Optional special days or blackout dates for this agent.</p>
                            </div>
                            <Button type="button" variant="outline" size="sm" disabled={useOrganizationSchedulingDefaults} onClick={() => setDateOverrides([...(dateOverrides || []), { date: '', enabled: false, label: '', ranges: [{ start: '09:00', end: '17:00' }] }])} className="rounded-lg gap-2">
                              <Plus className="w-4 h-4" /> Add override
                            </Button>
                          </div>
                          {(dateOverrides || []).map((override, overrideIndex) => (
                            <div key={`agent-override-${overrideIndex}`} className="border rounded-lg p-3 space-y-3">
                              <div className="grid grid-cols-1 md:grid-cols-[1fr,1fr,auto,auto] gap-3 items-end">
                                <div className="space-y-1.5">
                                  <Label>Date</Label>
                                  <Input type="date" value={override.date || ''} disabled={useOrganizationSchedulingDefaults} onChange={(e) => setDateOverrides((dateOverrides || []).map((item, i) => i === overrideIndex ? { ...item, date: e.target.value } : item))} className="rounded-lg" />
                                </div>
                                <div className="space-y-1.5">
                                  <Label>Label</Label>
                                  <Input value={override.label || ''} disabled={useOrganizationSchedulingDefaults} onChange={(e) => setDateOverrides((dateOverrides || []).map((item, i) => i === overrideIndex ? { ...item, label: e.target.value } : item))} className="rounded-lg" placeholder="Holiday / special hours" />
                                </div>
                                <div className="flex items-center gap-2 pb-2">
                                  <Switch checked={!!override.enabled} disabled={useOrganizationSchedulingDefaults} onCheckedChange={(value) => setDateOverrides((dateOverrides || []).map((item, i) => i === overrideIndex ? { ...item, enabled: value } : item))} />
                                  <span className="text-xs text-muted-foreground">{override.enabled ? 'Available' : 'Blocked'}</span>
                                </div>
                                <Button type="button" variant="ghost" size="icon" disabled={useOrganizationSchedulingDefaults} onClick={() => setDateOverrides((dateOverrides || []).filter((_, i) => i !== overrideIndex))}>
                                  <Trash2 className="w-4 h-4 text-destructive" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className="space-y-1.5">
                            <Label>Slot Duration (min)</Label>
                            <Input type="number" min="5" step="5" value={slotDurationMinutes} onChange={(e) => setSlotDurationMinutes(Number(e.target.value) || 30)} className="rounded-lg" disabled={useOrganizationSchedulingDefaults} />
                          </div>
                          <div className="space-y-1.5">
                            <Label>Slot Increment (min)</Label>
                            <Input type="number" min="5" step="5" value={slotIncrementMinutes} onChange={(e) => setSlotIncrementMinutes(Number(e.target.value) || 15)} className="rounded-lg" disabled={useOrganizationSchedulingDefaults} />
                          </div>
                          <div className="space-y-1.5">
                            <Label>Minimum Notice (min)</Label>
                            <Input type="number" min="0" step="5" value={minimumNoticeMinutes} onChange={(e) => setMinimumNoticeMinutes(Number(e.target.value) || 0)} className="rounded-lg" disabled={useOrganizationSchedulingDefaults} />
                          </div>
                          <div className="space-y-1.5">
                            <Label>Booking Window (days)</Label>
                            <Input type="number" min="1" step="1" value={bookingWindowDays} onChange={(e) => setBookingWindowDays(Number(e.target.value) || 30)} className="rounded-lg" disabled={useOrganizationSchedulingDefaults} />
                          </div>
                          <div className="space-y-1.5">
                            <Label>Buffer Before (min)</Label>
                            <Input type="number" min="0" step="5" value={bufferBeforeMinutes} onChange={(e) => setBufferBeforeMinutes(Number(e.target.value) || 0)} className="rounded-lg" disabled={useOrganizationSchedulingDefaults} />
                          </div>
                          <div className="space-y-1.5">
                            <Label>Buffer After (min)</Label>
                            <Input type="number" min="0" step="5" value={bufferAfterMinutes} onChange={(e) => setBufferAfterMinutes(Number(e.target.value) || 0)} className="rounded-lg" disabled={useOrganizationSchedulingDefaults} />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <Button type="button" variant="outline" className="rounded-lg" onClick={handlePreviewAvailability} disabled={previewingAvailability}>
                          {previewingAvailability ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Calendar className="w-4 h-4 mr-2" />}
                          Preview Availability
                        </Button>
                        <p className="text-xs text-muted-foreground">Uses Apollo scheduling rules and, for Google Calendar, real free/busy data when a provider connection is configured.</p>
                      </div>
                      {availabilityPreview && (
                        <div className="border rounded-xl p-4 bg-muted/20 space-y-3">
                          <div className="text-sm font-medium">Availability Preview</div>
                          <div className="text-xs text-muted-foreground">Provider: {availabilityPreview.provider} · Mode: {availabilityPreview.mode} · Timezone: {availabilityPreview.timezone}</div>
                          <div className="space-y-2">
                            {(availabilityPreview.slots || []).length === 0 ? (
                              <p className="text-sm text-muted-foreground">No slots available with the current rules.</p>
                            ) : (
                              availabilityPreview.slots.map((slot, idx) => (
                                <div key={`${slot.start}-${idx}`} className="text-sm rounded-lg border px-3 py-2 bg-background/80">
                                  {slot.display}
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="space-y-1.5">
                      <Label>Confirmation Message</Label>
                      <Textarea value={confirmationMessage} onChange={(e) => setConfirmationMessage(e.target.value)} placeholder="Your appointment has been scheduled! You'll receive a confirmation email shortly." className="rounded-lg resize-none" rows={3} data-testid="confirmation-message-input" />
                    </div>
                  </>
                )}

                {category === 'sales' && (
                  <>
                    <SectionHeader icon={TrendingUp} title="Sales Settings" description="Configure sales-specific behavior" />

                    <SliderField
                      label="Max Objection Attempts"
                      description="How many times to address a single objection before accepting it"
                      value={maxObjectionAttempts}
                      onChange={setMaxObjectionAttempts}
                      min={1} max={10} step={1}
                    />

                    <div className="border-t border-border pt-5">
                      <SectionHeader icon={MessageSquare} title="Qualification Questions" description="Questions the agent should ask to qualify leads" />
                      
                      {qualificationQuestions.length > 0 && (
                        <div className="space-y-2 mb-4">
                          {qualificationQuestions.map((q, i) => (
                            <div key={i} className="flex items-center gap-2 bg-secondary/50 rounded-lg p-2.5">
                              <Badge variant="outline" className="h-5 w-5 flex items-center justify-center p-0 text-[10px]">{i + 1}</Badge>
                              <span className="text-xs text-foreground flex-1">{q}</span>
                              <Button variant="ghost" size="sm" onClick={() => setQualificationQuestions(qualificationQuestions.filter((_, idx) => idx !== i))} className="h-6 w-6 p-0 text-destructive hover:text-destructive">
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      <div className="flex gap-2">
                        <Input value={newQualQuestion} onChange={(e) => setNewQualQuestion(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addQualificationQuestion()} placeholder="e.g., What's your current budget range?" className="rounded-lg text-sm" data-testid="qual-question-input" />
                        <Button variant="outline" onClick={addQualificationQuestion} disabled={!newQualQuestion.trim()} className="rounded-lg" data-testid="add-qual-question-btn">
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </>
                )}

                )}
              </TabsContent>

              {/* ============ KNOWLEDGE ============ */}
              <TabsContent value="knowledge" className="p-5 space-y-6 m-0">
                <SectionHeader icon={Database} title="Knowledge Base Sources" description="Attach the shared sources this agent brain can use. In most cases, one agent can cover questions, booking, and lead capture from the same shared knowledge base." />
                
                {knowledgeSources.length === 0 ? (
                  <div className="border border-border rounded-lg p-8 text-center">
                    <Database className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
                    <p className="text-muted-foreground text-sm mb-3">No knowledge sources available</p>
                    <Button variant="outline" className="rounded-lg" onClick={() => navigate('/knowledge-base')}>
                      Create Knowledge Source
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {knowledgeSources.map((source) => (
                      <div 
                        key={source.id}
                        className={`border rounded-lg p-4 flex items-center justify-between cursor-pointer transition-all ${
                          linkedSources.includes(source.id) 
                            ? 'border-primary bg-primary/5' 
                            : 'border-border hover:border-foreground/20'
                        }`}
                        onClick={() => {
                          if (linkedSources.includes(source.id)) {
                            setLinkedSources(linkedSources.filter(id => id !== source.id));
                          } else {
                            setLinkedSources([...linkedSources, source.id]);
                          }
                        }}
                        data-testid={`knowledge-source-${source.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <Checkbox checked={linkedSources.includes(source.id)} className="pointer-events-none" />
                          <div>
                            <p className="font-medium text-sm">{source.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {source.source_type.replace('_', ' ')} {source.entry_count != null && `| ${source.entry_count} entries`} {source.linked_agent_count != null && `| used by ${source.linked_agent_count} agent${source.linked_agent_count === 1 ? '' : 's'}`}
                              {source.visibility_scope ? ` | ${source.visibility_scope.replace('_', ' ')}` : ''}
                              {source.knowledge_group_key ? `:${source.knowledge_group_key}` : ''}
                              {source.is_inherited ? ' | inherited' : ''}
                            </p>
                          </div>
                        </div>
                        <Badge variant={source.status === 'synced' ? 'default' : 'secondary'} className="text-[10px]">
                          {source.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="rounded-xl border border-border bg-secondary/20 p-4 space-y-4">
                  <div>
                    <p className="font-medium text-sm text-foreground">Knowledge group membership</p>
                    <p className="text-xs text-muted-foreground mt-1">Use groups when multiple agents truly share the same knowledge. For most businesses, one primary agent with one shared knowledge base is enough.</p>
                  </div>
                  <div className="grid gap-3 md:grid-cols-[1fr_auto]">
                    <Input value={newKnowledgeGroupName} onChange={(e) => setNewKnowledgeGroupName(e.target.value)} placeholder="Create a new group here, e.g. Main Site Team" className="rounded-lg text-sm" />
                    <Button type="button" variant="outline" onClick={createKnowledgeGroupInline} disabled={creatingKnowledgeGroup || !newKnowledgeGroupName.trim()} className="rounded-lg">
                      {creatingKnowledgeGroup ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}<span className="ml-2">Create Group</span>
                    </Button>
                  </div>
                  <div className="text-xs text-muted-foreground">You can also manage all groups centrally in Knowledge Base → Groups.</div>
                  <div>
                    <p className="font-medium text-sm text-foreground">KB strategy for this agent</p>
                    <p className="text-xs text-muted-foreground mt-1">Apply the right knowledge footprint for the agent's role instead of giving every agent the full KB.</p>
                  </div>
                  <div className="grid md:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">KB mode</Label>
                      <Select value={kbMode} onValueChange={setKbMode}>
                        <SelectTrigger className="rounded-lg text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="full">Full KB</SelectItem>
                          <SelectItem value="minimal">Minimal KB</SelectItem>
                          <SelectItem value="scheduling_focused">Scheduling-focused KB</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Retrieval result limit</Label>
                      <Input type="number" min="1" max="8" value={kbMaxResults} onChange={(e) => setKbMaxResults(e.target.value)} className="rounded-lg text-sm" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Shared knowledge groups</Label>
                    {knowledgeGroups.length === 0 ? (
                      <div className="rounded-lg border border-dashed border-border p-3 text-xs text-muted-foreground">
                        No managed groups yet. Create them in Knowledge Base → Groups.
                      </div>
                    ) : (
                      <div className="space-y-2 rounded-lg border border-border p-3 bg-background/40">
                        {knowledgeGroups.map((group) => {
                          const checked = sharedGroupKeys.includes(group.group_key);
                          return (
                            <label key={group.id} className="flex items-start gap-3 cursor-pointer">
                              <Checkbox
                                checked={checked}
                                onCheckedChange={(next) => {
                                  if (next) setSharedGroupKeys([...sharedGroupKeys, group.group_key]);
                                  else setSharedGroupKeys(sharedGroupKeys.filter((key) => key !== group.group_key));
                                }}
                              />
                              <div>
                                <div className="text-sm font-medium">{group.name}</div>
                                <div className="text-xs text-muted-foreground">{group.group_key}{group.description ? ` — ${group.description}` : ''}</div>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground">Agents inherit shared-group knowledge sources from the groups checked here, in addition to their direct sources.</p>
                    {sharedGroupKeys.length > 0 && (
                      <div className="rounded-lg border border-emerald-200 bg-emerald-50 dark:bg-emerald-950/20 dark:border-emerald-800 p-3 text-xs text-emerald-700 dark:text-emerald-300">
                        This agent currently inherits knowledge from: {sharedGroupKeys.join(', ')}
                      </div>
                    )}
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Operator note</Label>
                    <Textarea rows={3} value={kbNotes} onChange={(e) => setKbNotes(e.target.value)} placeholder="Why this agent should use this KB shape" />
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {category === 'routing' ? 'Routing agent: keep this light and rule-focused.' : category === 'appointment_setting' ? 'Scheduling agent: keep this narrow and booking-focused.' : 'Informational/specialist agent: broader KB is appropriate.'}
                  </div>
                </div>

                {linkedSources.length > 0 && (
                  <p className="text-xs text-primary">
                    {linkedSources.length} source(s) linked to this agent
                  </p>
                )}
              </TabsContent>

              {/* ============ HANDOFF RULES ============ */}
              <TabsContent value="living-files" className="p-5 space-y-6 m-0">
                <div className="rounded-xl border border-border bg-card p-4 space-y-3">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="text-lg font-semibold">Editable Agent Files</h3>
                      <p className="text-sm text-muted-foreground">
                        These are the OpenClaw-style living files for the agent: soul, identity, heartbeat, tools, and user context. Users can edit them here, and agents can read/write the same stored files through Apollo.
                      </p>
                    </div>
                    <Button onClick={handleSaveEditableFiles} disabled={!agentId || savingEditableFiles} className="rounded-lg gap-2">
                      {savingEditableFiles ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      Save Files
                    </Button>
                  </div>
                  {!agentId ? (
                    <div className="rounded-lg border border-dashed border-border p-3 text-sm text-muted-foreground">
                      Save the agent once first, then these living files become editable.
                    </div>
                  ) : null}
                </div>

                {Object.entries(editableFiles).map(([fileName, fileContent]) => (
                  <div key={fileName} className="space-y-2">
                    <Label>{fileName}</Label>
                    <Textarea
                      value={fileContent}
                      onChange={(e) => setEditableFiles(prev => ({ ...prev, [fileName]: e.target.value }))}
                      placeholder={`Edit ${fileName}`}
                      className="min-h-[220px] font-mono text-sm"
                      disabled={!agentId}
                    />
                  </div>
                ))}
              </TabsContent>

              <TabsContent value="handoff" className="p-5 space-y-6 m-0">
                <SectionHeader icon={Zap} title="Advanced Agent Handoff System" description="Optional advanced routing for teams that truly need multiple specialized agents" />

                {category === 'routing' && (() => {
                  const linkedTargets = [...new Set(handoffRules.map(r => r.target_agent_id).filter(id => id && id.trim()))];
                  return linkedTargets.length < 2 ? (
                    <div className="rounded-xl border border-blue-200 bg-blue-50 dark:bg-blue-950/20 dark:border-blue-800 p-4 flex items-start gap-3">
                      <AlertTriangle className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                      <div className="text-sm text-blue-800 dark:text-blue-300">
                        <p className="font-semibold">Routing Agent requires at least 2 destination agents</p>
                        <p className="mt-0.5">Add handoff rules below pointing to {2 - linkedTargets.length} more distinct agent(s) before this routing agent can become active.</p>
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-xl border border-green-200 bg-green-50 dark:bg-green-950/20 dark:border-green-800 p-3 flex items-center gap-2 text-sm text-green-700 dark:text-green-300">
                      <Check className="w-4 h-4 flex-shrink-0" />
                      <span>{linkedTargets.length} destination agents linked — routing is ready.</span>
                    </div>
                  );
                })()}
                
                <div className="bg-secondary/30 border border-border rounded-xl p-4 text-xs text-muted-foreground space-y-1.5">
                  <p className="font-medium text-foreground text-sm">How handoff works:</p>
                  <p><strong>Keyword rules</strong> match specific phrases in the user's message (e.g., "speak to sales").</p>
                  <p><strong>Intent rules</strong> use AI to detect conversation direction (e.g., the user shifts from asking questions to showing purchase interest). The LLM detects these automatically.</p>
                  <p>When a handoff triggers, the conversation transfers to the target agent with full context.</p>
                </div>

                <div className="space-y-2">
                  <Label>Handoff announcement style</Label>
                  <p className="text-xs text-muted-foreground">Control whether visitors notice the switch or stay with the same visible persona.</p>
                  <Select value={handoffStyle} onValueChange={setHandoffStyle}>
                    <SelectTrigger className="rounded-lg text-sm" data-testid="handoff-style-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="seamless">Seamless</SelectItem>
                      <SelectItem value="announce_on_name_change">Announce on display-name change</SelectItem>
                      <SelectItem value="always_announce">Always announce</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Keyword Rules */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-sm text-foreground">Keyword Triggers</h4>
                      <p className="text-xs text-muted-foreground">Transfer when a message contains specific words or phrases</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => addHandoffRule('keyword')} className="rounded-lg gap-1.5 text-xs" data-testid="add-keyword-rule">
                      <Plus className="w-3 h-3" /> Add Keyword Rule
                    </Button>
                  </div>
                  
                  {handoffRules.filter(r => r.type === 'keyword' || !r.type).map((rule, realIndex) => {
                    const index = handoffRules.indexOf(rule);
                    return (
                      <div key={index} className="border border-border rounded-lg p-4 space-y-3 bg-card">
                        <div className="grid grid-cols-3 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs">Trigger Phrase</Label>
                            <Input value={rule.trigger || ''} onChange={(e) => updateHandoffRule(index, 'trigger', e.target.value)} placeholder="e.g., speak to sales" className="rounded-lg text-sm" data-testid={`keyword-trigger-${index}`} />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Condition</Label>
                            <Select value={rule.condition || 'contains'} onValueChange={(v) => updateHandoffRule(index, 'condition', v)}>
                              <SelectTrigger className="rounded-lg text-sm"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="contains">Contains</SelectItem>
                                <SelectItem value="starts_with">Starts with</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Route To</Label>
                            {targetAgentOptions.length > 0 ? (
                              <Select value={rule.target_agent_id || ''} onValueChange={(v) => updateHandoffRule(index, 'target_agent_id', v)}>
                                <SelectTrigger className="rounded-lg text-sm" data-testid={`keyword-target-${index}`}>
                                  <SelectValue placeholder="Select agent" />
                                </SelectTrigger>
                                <SelectContent>
                                  {targetAgentOptions.map(a => (
                                    <SelectItem key={a.id} value={a.id}>
                                      <span className="flex items-center gap-2">
                                        <Badge variant="outline" className="text-[9px] px-1.5">{a.category.replace('_', ' ')}</Badge>
                                        {getAgentBackendName(a)}
                                      </span>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <Input value={rule.target_agent_id || ''} onChange={(e) => updateHandoffRule(index, 'target_agent_id', e.target.value)} placeholder="Agent ID" className="rounded-lg text-sm" />
                            )}
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => removeHandoffRule(index)} className="text-destructive hover:text-destructive hover:bg-destructive/10 rounded-lg text-xs">
                          <Trash2 className="w-3 h-3 mr-1" /> Remove
                        </Button>
                      </div>
                    );
                  })}

                  {handoffRules.filter(r => r.type === 'keyword' || !r.type).length === 0 && (
                    <div className="border border-dashed border-border rounded-lg p-6 text-center">
                      <p className="text-xs text-muted-foreground">No keyword rules configured</p>
                    </div>
                  )}
                </div>

                {/* Intent Rules */}
                <div className="space-y-3 border-t border-border pt-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-sm text-foreground flex items-center gap-2">
                        Intent Detection
                        <Badge className="text-[9px]">AI-Powered</Badge>
                      </h4>
                      <p className="text-xs text-muted-foreground">AI analyzes conversation context and detects when to transfer</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => addHandoffRule('intent')} className="rounded-lg gap-1.5 text-xs" data-testid="add-intent-rule">
                      <Plus className="w-3 h-3" /> Add Intent Rule
                    </Button>
                  </div>
                  
                  {handoffRules.filter(r => r.type === 'intent').map((rule) => {
                    const index = handoffRules.indexOf(rule);
                    return (
                      <div key={index} className="border border-border rounded-lg p-4 space-y-3 bg-card">
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs">Detect Intent</Label>
                            <Select value={rule.intent || ''} onValueChange={(v) => updateHandoffRule(index, 'intent', v)}>
                              <SelectTrigger className="rounded-lg text-sm" data-testid={`intent-select-${index}`}>
                                <SelectValue placeholder="Select an intent" />
                              </SelectTrigger>
                              <SelectContent>
                                {availableIntents.map(i => (
                                  <SelectItem key={i.value} value={i.value}>
                                    <div>
                                      <span className="font-medium">{i.label}</span>
                                      <span className="text-muted-foreground text-xs ml-2">- {i.desc}</span>
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Route To</Label>
                            {targetAgentOptions.length > 0 ? (
                              <Select value={rule.target_agent_id || ''} onValueChange={(v) => updateHandoffRule(index, 'target_agent_id', v)}>
                                <SelectTrigger className="rounded-lg text-sm" data-testid={`intent-target-${index}`}>
                                  <SelectValue placeholder="Select agent" />
                                </SelectTrigger>
                                <SelectContent>
                                  {targetAgentOptions.map(a => (
                                    <SelectItem key={a.id} value={a.id}>
                                      <span className="flex items-center gap-2">
                                        <Badge variant="outline" className="text-[9px] px-1.5">{a.category.replace('_', ' ')}</Badge>
                                        {getAgentBackendName(a)}
                                      </span>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <Input value={rule.target_agent_id || ''} onChange={(e) => updateHandoffRule(index, 'target_agent_id', e.target.value)} placeholder="Agent ID" className="rounded-lg text-sm" />
                            )}
                          </div>
                        </div>
                        {rule.intent && (
                          <p className="text-[11px] text-muted-foreground bg-secondary/50 rounded-lg px-3 py-2">
                            {availableIntents.find(i => i.value === rule.intent)?.desc}
                          </p>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => removeHandoffRule(index)} className="text-destructive hover:text-destructive hover:bg-destructive/10 rounded-lg text-xs">
                          <Trash2 className="w-3 h-3 mr-1" /> Remove
                        </Button>
                      </div>
                    );
                  })}

                  {handoffRules.filter(r => r.type === 'intent').length === 0 && (
                    <div className="border border-dashed border-border rounded-lg p-6 text-center">
                      <p className="text-xs text-muted-foreground">No intent rules configured. Add one to enable AI-powered conversation routing.</p>
                    </div>
                  )}
                </div>

                {/* How it works visually */}
                {handoffRules.length > 0 && targetAgentOptions.length > 0 && (
                  <div className="border-t border-border pt-5">
                    <h4 className="font-semibold text-sm text-foreground mb-3">Active Routing Map</h4>
                    <div className="space-y-2">
                      {handoffRules.filter(r => r.target_agent_id).map((rule, i) => {
                        const target = targetAgentOptions.find(a => a.id === rule.target_agent_id);
                        return (
                          <div key={i} className="flex items-center gap-2 text-xs bg-secondary/30 rounded-lg p-2.5">
                            <Badge variant="outline" className="text-[10px]">{effectiveAgentName}</Badge>
                            <span className="text-muted-foreground">
                              {rule.type === 'intent' ? `detects "${rule.intent?.replace('_', ' ')}"` : `hears "${rule.trigger}"`}
                            </span>
                            <Zap className="w-3 h-3 text-primary" />
                            <Badge className="text-[10px]">{target?.name || rule.target_agent_id.slice(0, 8)}</Badge>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </TabsContent>

              {/* ============ WIDGET & EMBED ============ */}
              <TabsContent value="widget" className="p-5 space-y-6 m-0">
                <SectionHeader icon={Code2} title="Web Chat Deployment" description="Configure how this agent brain appears on your website. Other deployment surfaces like SMS can reuse the same agent." />

                <div className="rounded-xl border border-border bg-card/60 p-4 space-y-3">
                  <div>
                    <p className="font-medium text-sm text-foreground">Deployment surfaces</p>
                    <p className="text-xs text-muted-foreground mt-1">This agent can be reused across multiple surfaces. Right now Apollo directly supports the website widget here, and SMS through Twilio in Settings.</p>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="rounded-lg border border-border bg-background/60 p-3">
                      <p className="text-sm font-medium">Web chat widget</p>
                      <p className="text-xs text-muted-foreground mt-1">Native embedded website chat with widget styling, preview, and site context support.</p>
                    </div>
                    <div className="rounded-lg border border-border bg-background/60 p-3">
                      <p className="text-sm font-medium">SMS deployment</p>
                      <p className="text-xs text-muted-foreground mt-1">Connect Twilio in Settings, then use the same agent brain for text-message conversations when applicable.</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Speech Bubble Animation</Label>
                  <div className="grid grid-cols-4 gap-2">
                    {[
                      { value: 'bounce', label: 'Bounce' },
                      { value: 'pulse', label: 'Pulse' },
                      { value: 'wave', label: 'Wave' },
                      { value: 'none', label: 'None' },
                    ].map(anim => (
                      <button
                        key={anim.value}
                        type="button"
                        onClick={() => setBubbleAnimation(anim.value)}
                        className={`p-2.5 border rounded-lg text-center text-xs transition-all ${
                          bubbleAnimation === anim.value ? 'border-primary bg-primary/5 font-medium' : 'border-border hover:border-foreground/20'
                        }`}
                        data-testid={`animation-${anim.value}`}
                      >
                        {anim.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Widget Position</Label>
                  <Select value={widgetPosition} onValueChange={setWidgetPosition}>
                    <SelectTrigger className="rounded-lg" data-testid="widget-position-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bottom-right">Bottom Right</SelectItem>
                      <SelectItem value="bottom-left">Bottom Left</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Primary Color</Label>
                  <div className="flex items-center gap-3">
                    <input type="color" value={widgetColor} onChange={(e) => setWidgetColor(e.target.value)} className="w-10 h-10 rounded-lg border border-border cursor-pointer" data-testid="widget-color-input" />
                    <Input value={widgetColor} onChange={(e) => setWidgetColor(e.target.value)} className="rounded-lg font-mono text-sm flex-1" />
                  </div>
                  <p className="text-xs text-muted-foreground">This drives the default header, action button, launcher, and message accent colors.</p>
                </div>

                <div className="space-y-2">
                  <Label>Widget Theme</Label>
                  <Select value={widgetThemeMode} onValueChange={setWidgetThemeMode}>
                    <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="light">Light</SelectItem>
                      <SelectItem value="dark">Dark</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">Pick a clean default base, then only override details if you need to.</p>
                </div>

                <div className="space-y-3 rounded-xl border border-border bg-card/50 p-4">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <Label>Advanced Appearance</Label>
                      <p className="text-xs text-muted-foreground mt-1">Leave this off to auto-derive most colors from the primary color and theme.</p>
                    </div>
                    <Switch checked={advancedStyling} onCheckedChange={setAdvancedStyling} />
                  </div>
                  {advancedStyling ? (
                    <>
                      <div className="flex items-center justify-between rounded-lg bg-secondary/40 px-3 py-2 text-xs text-muted-foreground">
                        <span>Editing detailed widget colors</span>
                        <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={resetWidgetThemeOverrides}>Reset overrides</Button>
                      </div>
                      <div className="grid gap-4 md:grid-cols-2">
                        {[
                          ['header_background', 'Header background'],
                          ['header_text', 'Header text'],
                          ['panel_background', 'Panel background'],
                          ['panel_border', 'Panel border'],
                          ['body_background', 'Chat background'],
                          ['body_text', 'Body text'],
                          ['muted_text', 'Muted text'],
                          ['assistant_bubble_background', 'Assistant bubble'],
                          ['assistant_bubble_text', 'Assistant text'],
                          ['user_bubble_background', 'User bubble'],
                          ['user_bubble_text', 'User text'],
                          ['input_background', 'Input background'],
                          ['input_text', 'Input text'],
                          ['input_border', 'Input border'],
                          ['send_button_background', 'Send button'],
                          ['send_button_text', 'Send button text'],
                          ['launcher_background', 'Launcher background'],
                          ['launcher_icon', 'Launcher icon'],
                          ['greeting_background', 'Greeting bubble'],
                          ['greeting_text', 'Greeting text'],
                          ['quick_select_background', 'Quick select background'],
                          ['quick_select_text', 'Quick select text'],
                          ['quick_select_border', 'Quick select border'],
                        ].map(([key, label]) => (
                          <div key={key} className="space-y-1.5">
                            <Label className="text-xs">{label}</Label>
                            <div className="flex items-center gap-3">
                              <input type="color" value={derivedWidgetTheme[key]} onChange={(e) => updateWidgetThemeOverride(key, e.target.value)} className="w-10 h-10 rounded-lg border border-border cursor-pointer" />
                              <Input value={advancedStyling ? (widgetThemeOverrides[key] || derivedWidgetTheme[key]) : derivedWidgetTheme[key]} onChange={(e) => updateWidgetThemeOverride(key, e.target.value)} className="rounded-lg font-mono text-sm flex-1" />
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : null}
                </div>

                <div className="space-y-3 rounded-xl border border-border bg-card/50 p-4">
                  <div>
                    <Label>Response Delivery</Label>
                    <p className="text-xs text-muted-foreground mt-1">Control whether the widget breaks long replies into chat-sized chunks and shows typing between them.</p>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Chunk long replies</Label>
                      <p className="text-xs text-muted-foreground">Break long assistant replies into smaller thought-sized bubbles.</p>
                    </div>
                    <Switch checked={chunkResponses} onCheckedChange={setChunkResponses} />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Typing animation</Label>
                      <p className="text-xs text-muted-foreground">Show typing pauses between reply chunks.</p>
                    </div>
                    <Switch checked={typingAnimation} onCheckedChange={setTypingAnimation} />
                  </div>
                  <div className="space-y-2">
                    <Label>Typing pace</Label>
                    <Select value={typingPace} onValueChange={setTypingPace}>
                      <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fast">Fast</SelectItem>
                        <SelectItem value="normal">Normal</SelectItem>
                        <SelectItem value="slow">Slow</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label>Show Avatar</Label>
                    <p className="text-xs text-muted-foreground">Display a bot avatar in the widget</p>
                  </div>
                  <Switch checked={showAvatar} onCheckedChange={setShowAvatar} data-testid="show-avatar-toggle" />
                </div>

                <div className="space-y-3 rounded-xl border border-border bg-card/50 p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <Label>Widget Avatar</Label>
                      <p className="text-xs text-muted-foreground mt-1">Upload an avatar for the web chat widget. Save the agent first to enable uploads.</p>
                    </div>
                    {avatarUploading ? <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /> : null}
                  </div>
                  <div className="flex items-center gap-4">
                    <AgentAvatar
                      name={name || 'Agent'}
                      avatarUrl={avatarUrl || ''}
                      className="h-16 w-16 border border-border"
                      fallbackClassName="bg-primary/10 text-primary font-semibold text-lg"
                    />
                    <div className="flex flex-wrap gap-2">
                      <input
                        ref={avatarInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/gif"
                        className="hidden"
                        onChange={handleAvatarSelected}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        className="rounded-lg gap-2"
                        onClick={() => avatarInputRef.current?.click()}
                        disabled={!isEditing || avatarUploading}
                      >
                        <ImagePlus className="w-4 h-4" />
                        {avatarUrl ? 'Replace image' : 'Upload image'}
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        className="rounded-lg gap-2 text-destructive hover:text-destructive"
                        onClick={handleRemoveAvatar}
                        disabled={!isEditing || !avatarUrl || avatarUploading}
                      >
                        <X className="w-4 h-4" />
                        Remove
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Widget Preview */}
                <div className="border-t border-border pt-5">
                  <Label className="mb-3 block">Preview</Label>
                  <div className="bg-secondary/30 rounded-xl p-6 relative h-[420px] overflow-hidden">
                    <div className="absolute text-[10px] text-muted-foreground top-3 left-3">Your website</div>
                    <div className="absolute left-1/2 top-10 h-[320px] w-[320px] max-w-[calc(100%-3rem)] -translate-x-1/2 rounded-2xl border shadow-xl overflow-hidden" style={{ backgroundColor: derivedWidgetTheme.panel_background, borderColor: derivedWidgetTheme.panel_border }}>
                      <div className="flex items-center justify-between px-4 py-3" style={{ backgroundColor: derivedWidgetTheme.header_background, color: derivedWidgetTheme.header_text }}>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full overflow-hidden flex items-center justify-center" style={{ backgroundColor: 'rgba(255,255,255,.18)' }}>
                            {showAvatar && avatarUrl ? <img src={avatarUrl} alt="Agent avatar preview" className="w-full h-full object-cover" /> : <Bot className="w-4 h-4" />}
                          </div>
                          <div>
                            <div className="text-xs font-semibold">{displayName || name || 'Agent'}</div>
                            <div className="text-[10px] opacity-80">Online</div>
                          </div>
                        </div>
                        <X className="w-4 h-4 opacity-80" />
                      </div>
                      <div className="px-4 py-3 space-y-2" style={{ backgroundColor: derivedWidgetTheme.body_background, color: derivedWidgetTheme.body_text }}>
                        <div className={`rounded-xl border p-3 text-xs max-w-[180px] shadow-sm widget-bubble-${bubbleAnimation}`} style={{ backgroundColor: derivedWidgetTheme.greeting_background, borderColor: derivedWidgetTheme.panel_border, color: derivedWidgetTheme.greeting_text }}>
                          {greetingMessage || 'Hi! How can I help you today?'}
                        </div>
                        <div className="rounded-2xl rounded-bl-md p-3 text-xs max-w-[180px] border" style={{ backgroundColor: derivedWidgetTheme.assistant_bubble_background, borderColor: derivedWidgetTheme.panel_border, color: derivedWidgetTheme.assistant_bubble_text }}>
                          Happy to help.
                        </div>
                        <div className="rounded-2xl rounded-br-md p-3 text-xs max-w-[180px] ml-auto" style={{ backgroundColor: derivedWidgetTheme.user_bubble_background, color: derivedWidgetTheme.user_bubble_text }}>
                          Tell me more
                        </div>
                      </div>
                      <div className="px-4 py-3 flex items-center gap-2 border-t" style={{ backgroundColor: derivedWidgetTheme.panel_background, borderColor: derivedWidgetTheme.panel_border }}>
                        <div className="flex-1 rounded-full px-3 py-2 text-[11px] border" style={{ backgroundColor: derivedWidgetTheme.input_background, borderColor: derivedWidgetTheme.input_border, color: derivedWidgetTheme.muted_text }}>
                          Type a message...
                        </div>
                        <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ backgroundColor: derivedWidgetTheme.send_button_background, color: derivedWidgetTheme.send_button_text }}>
                          <MessageSquare className="w-4 h-4" />
                        </div>
                      </div>
                    </div>
                    <div 
                      className={`absolute ${widgetPosition === 'bottom-right' ? 'right-4 bottom-4' : 'left-4 bottom-4'}`}
                    >
                      <div 
                        className={`w-12 h-12 rounded-full flex items-center justify-center shadow-lg cursor-pointer ml-auto overflow-hidden widget-btn-${bubbleAnimation}`}
                        style={{ backgroundColor: derivedWidgetTheme.launcher_background, color: derivedWidgetTheme.launcher_icon }}
                      >
                        {showAvatar ? (
                          avatarUrl ? (
                            <img src={avatarUrl} alt="Agent avatar preview" className="w-full h-full object-cover" />
                          ) : (
                            <Bot className="w-6 h-6" />
                          )
                        ) : (
                          <MessageSquare className="w-6 h-6" />
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {isEditing && embedCode && (
                  <div className="border-t border-border pt-5 space-y-4">
                    <Label className="mb-2 block">Embed Code</Label>
                    <p className="text-xs text-muted-foreground mb-3">Add this snippet before the closing <code className="bg-secondary px-1.5 py-0.5 rounded text-[11px] font-mono">&lt;/body&gt;</code> tag of your website</p>
                    <div className="relative">
                      <pre className="bg-secondary rounded-lg p-3 text-xs font-mono overflow-x-auto text-foreground whitespace-pre-wrap break-all">{embedCode}</pre>
                      <Button variant="outline" size="sm" className="absolute top-2 right-2 rounded-lg gap-1.5 bg-card" onClick={handleCopyEmbed} data-testid="copy-embed-button">
                        {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                        {copied ? 'Copied!' : 'Copy'}
                      </Button>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" className="flex-1 rounded-lg gap-2" onClick={() => navigate(`/agents/${agentId}/widget`)} data-testid="open-distribution-page">
                        <Code2 className="w-4 h-4" /> Distribution Page
                      </Button>
                      <Button variant="outline" className="flex-1 rounded-lg gap-2" onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/widget/${agentId}/preview`, '_blank')} data-testid="open-live-preview">
                        <ExternalLink className="w-4 h-4" /> Live Preview
                      </Button>
                    </div>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AgentBuilderPage;

